/**
 * OEM+ derivation engine — the pure port of `OEM+_Platform_Demo.html`'s
 * derivation layer.
 *
 * Every export here is a literal port of one demo function or data table, with
 * the demo's arithmetic, rounding, magic numbers, tie-breaks and sort
 * comparators kept exactly as written. Each carries a `Demo line N` comment
 * naming its origin in the source HTML.
 *
 * ## The eager / lazy data contract
 *
 * The demo is one script with global `FLEET`, `HEALTH`, `WOS`, `SD`, `DM`,
 * `EVD` and a `B` object holding everything else. This port splits that in two:
 *
 * - **Eager** — `../data/core.ts` is imported directly and is always available
 *   on first paint. Functions that need only the fleet, the mines, the model
 *   classes, the rate card, the condition register or the per-machine cost row
 *   read it at module scope, exactly as the demo does, and keep the demo's
 *   signature.
 * - **Lazy** — everything else arrives through `useOemData()` at render time.
 *   A function that needs work orders, shift records, Maximo records,
 *   technician jobs, inventory, parts, RMS, telemetry or costs **takes that
 *   data as an explicit context parameter**. Contexts are small and composed
 *   with `&`; there is no god-object context.
 *
 * ## Context interface → `useOemData()` domain
 *
 * | Context | Domain | Key |
 * |---|---|---|
 * | {@link WorkOrderCtx} | `'work-orders'` | `wos` (columnar — `unpackTable`) |
 * | {@link ShiftCtx} | `'telemetry'` | `shift_daily` (columnar) |
 * | {@link DailyMetricCtx} | `'telemetry'` | `daily_metric` (columnar) |
 * | {@link InventoryCtx} | `'parts'` | `inventory` |
 * | {@link PartsCatalogueCtx} | `'parts'` | `parts` |
 * | {@link OrderCtx} | `'parts'` | `orders` |
 * | {@link BulletinCtx} | `'parts'` | `bulletins` |
 * | {@link TechJobCtx} | `'service'` | `tapp_jobs` |
 * | {@link TechPresenceCtx} | `'people'` | `tapp_techs` |
 * | {@link CaseCtx} | `'service'` | `cases` |
 * | {@link TrainingCtx} | `'service'` | `lms` |
 * | {@link ContractCtx} | `'service'` | `mx_contracts` |
 * | {@link RemoteSessionCtx} | `'service'` | `xreach_sessions` |
 * | {@link MaximoCtx} | `maximo/<ID>.json` (per machine, `loadMachineRecord`) | `mx` |
 * | {@link TimesheetCtx} | `'costs'` | `timesheets` |
 * | {@link MachineCostCategoryCtx} | `'costs'` | `cost_by_machine_cat` |
 * | {@link CssCtx} | `'people'` | `css` (columnar) |
 * | {@link RosterCtx} | `'people'` | `roster` (columnar) |
 * | {@link RmsAlertCtx} | `'rms'` | `rms_alerts` |
 * | {@link DocumentCtx} | `'doc-index'` | `docs` |
 * | {@link LakeCtx} | `lake/index.json` (`loadLakeIndex`) | `entries` |
 *
 * A scene author reads a function's context type to know which domains to load
 * before calling it. Narrative seeds (`ALERTS`, `INCIDENTS`) are imported
 * directly — they are compiled into the bundle, not fetched.
 *
 * ## The frozen clock
 *
 * The dataset's "now" is **09 Sep 2026 06:00 AEST**. Nothing here calls
 * the wall clock, a zero-argument date constructor, or a random number. Time comes
 * from `./oem-clock.ts` (`OEM_TODAY`, `daysTo()`); the demo's own hard-coded
 * date literals (`'2026-08-31'`, `'2026-09-16'`, `'2025-09-09'`, …) are kept
 * verbatim because they are part of the data, not of the clock.
 *
 * ## Purity
 *
 * No React, no side effects, no module-level mutable state, no memo caches, no
 * logging, no HTML. The demo's `jobs()` memoised into a module-level `JOBS`
 * cache; {@link jobs} here is a pure function of its context and allocates
 * afresh every call. Where the demo returned template-literal markup, this
 * module returns typed data; where it returned a mixed tuple of figures and
 * labels, this module returns a named interface.
 *
 * Engine layer (layer 2 of the 4-layer architecture).
 *
 * @see scripts/oem/README.md — which JSON file carries which dataset
 * @see docs/foundations/4-layer-architecture.md
 */

import {
  BY_ID,
  COST_PER_MACHINE,
  FLEET,
  HEALTH,
  MINES,
  MINE_ORDER,
  RATES,
  WORKINGS,
  type ComponentHealth,
  type MachineCost,
} from '../data/core.ts';
import type { RmsAlert } from '../schema/alert.ts';
import type { CostByMachineCategory, Timesheet } from '../schema/cost.ts';
import type { LakeEntry, LibraryDocument } from '../schema/document.ts';
import {
  ATTENTION_BANDS,
  CONDITION_BANDS,
  CONDITION_BAND_ORDER,
  bandOf,
  type ConditionBand,
  type ConditionBandSpec,
  type DivisionKey,
  type DivisionName,
  type Machine,
  type MachineClass,
  type MineCode,
  type ModelCode,
  type Subclass,
} from '../schema/machine.ts';
import type { Bulletin, CataloguePart, InventoryLine, PartOrder } from '../schema/part.ts';
import type { CustomerSatisfactionResponse, RosterEntry, TechnicianPresence } from '../schema/person.ts';
import type { OemPersonaId } from '../schema/persona.ts';
import type {
  MaximoAsset,
  MaximoContract,
  RemoteSession,
  ServiceCase,
  TechnicianJob,
  TrainingRecord,
} from '../schema/service.ts';
import type { DailyMetricRow, ShiftDailyRow } from '../schema/telemetry.ts';
import type { WorkOrder } from '../schema/work-order.ts';
import { OEM_TODAY, daysTo } from './oem-clock.ts';
import { act, type OemAction } from './seed/action.ts';
import { ALERTS, type Alert } from './seed/alerts.ts';
import { INCIDENTS, type Incident, type IncidentKpiValue } from './seed/incidents.ts';
import { metricInfoFor, type MetricInfo } from './seed/metric-info.ts';
import type { FleetRule } from './seed/workflow-steps.ts';

// ===========================================================================
// Context interfaces — the lazy data contract
// ===========================================================================

/** `useOemData('work-orders').wos`, unpacked. */
export interface WorkOrderCtx {
  readonly workOrders: readonly WorkOrder[];
}

/** `useOemData('telemetry').shift_daily`, unpacked. */
export interface ShiftCtx {
  readonly shiftDaily: readonly ShiftDailyRow[];
}

/** `useOemData('telemetry').daily_metric`, unpacked. */
export interface DailyMetricCtx {
  readonly dailyMetric: readonly DailyMetricRow[];
}

/** `useOemData('parts').inventory`. */
export interface InventoryCtx {
  readonly inventory: readonly InventoryLine[];
}

/** `useOemData('parts').parts`. */
export interface PartsCatalogueCtx {
  readonly parts: readonly CataloguePart[];
}

/** `useOemData('parts').orders`. */
export interface OrderCtx {
  readonly orders: readonly PartOrder[];
}

/** `useOemData('parts').bulletins`. */
export interface BulletinCtx {
  readonly bulletins: readonly Bulletin[];
}

/** `useOemData('service').tapp_jobs`. */
export interface TechJobCtx {
  readonly techJobs: readonly TechnicianJob[];
}

/** `useOemData('people').tapp_techs` — it ships with the people file, not service. */
export interface TechPresenceCtx {
  readonly techs: readonly TechnicianPresence[];
}

/** `useOemData('service').cases`. */
export interface CaseCtx {
  readonly cases: readonly ServiceCase[];
}

/** `useOemData('service').lms`. */
export interface TrainingCtx {
  readonly training: readonly TrainingRecord[];
}

/** `useOemData('service').mx_contracts`. */
export interface ContractCtx {
  readonly contracts: readonly MaximoContract[];
}

/** `useOemData('service').xreach_sessions`. */
export interface RemoteSessionCtx {
  readonly remoteSessions: readonly RemoteSession[];
}

/**
 * The demo's `B.mx` — machine id → Meridian Maximo asset record.
 *
 * The port ships one file per machine (`public/data/oem/maximo/<ID>.json`), so
 * a scene assembles this map from the records it has loaded. A missing entry is
 * `undefined`, which {@link mxOf} normalises to `null` exactly as the demo's
 * `B.mx[id] || null` does.
 */
export interface MaximoCtx {
  readonly maximo: Readonly<Record<string, MaximoAsset | undefined>>;
}

/** `useOemData('costs').timesheets`. */
export interface TimesheetCtx {
  readonly timesheets: readonly Timesheet[];
}

/** `useOemData('costs').cost_by_machine_cat`. */
export interface MachineCostCategoryCtx {
  readonly costByMachineCat: readonly CostByMachineCategory[];
}

/** `useOemData('people').css`, unpacked. */
export interface CssCtx {
  readonly css: readonly CustomerSatisfactionResponse[];
}

/** `useOemData('people').roster`, unpacked. */
export interface RosterCtx {
  readonly roster: readonly RosterEntry[];
}

/** `useOemData('rms').rms_alerts`. */
export interface RmsAlertCtx {
  readonly rmsAlerts: readonly RmsAlert[];
}

/** `useOemData('doc-index').docs`. */
export interface DocumentCtx {
  readonly documents: readonly LibraryDocument[];
}

/**
 * A lake index entry as {@link siteDocs} reads it.
 *
 * The demo filtered `B.extra`, where each entry carried its own `text`. The
 * port's `lake/index.json` keeps payloads out of the index, so `text` is
 * optional — a scene that has loaded whole lake documents can supply it, and
 * the demo's third match clause simply never fires when it has not.
 */
export type LakeIndexEntry = LakeEntry & { readonly text?: string };

/** `loadLakeIndex().entries`. */
export interface LakeCtx {
  readonly lakeEntries: readonly LakeIndexEntry[];
}

// ===========================================================================
// Formatting
// ===========================================================================

/**
 * Locale pinned for the demo's bare `toLocaleString()` calls.
 *
 * The demo ran in a browser and took the host locale. Pinning to `en-AU` makes
 * every ported figure deterministic across machines and CI; `en-AU` and `en-US`
 * produce the same comma grouping, which is what the demo's screenshots show.
 */
const OEM_LOCALE = 'en-AU';

/** `n.toLocaleString()` — the demo's thousands grouping, locale-pinned. */
export const groupNum = (n: number): string => n.toLocaleString(OEM_LOCALE);

/** Demo line 1855. `fmt$` — whole dollars, grouped, `A$` prefixed. */
export const fmt$ = (n: number): string => `A$${groupNum(Math.round(n))}`;

/**
 * Demo line 1856. `fmtk` — millions to two decimals, otherwise whole thousands.
 * Note the thousands branch does **not** group: `A$1234k` is the demo's output.
 */
export const fmtk = (n: number): string =>
  n >= 1e6 ? `A$${(n / 1e6).toFixed(2)}m` : `A$${Math.round(n / 1000)}k`;

// ===========================================================================
// Fleet, divisions and condition
// ===========================================================================

/** Demo line 1841. `SUBS` — the two subclasses of each division. */
export const SUBS: Readonly<Record<DivisionKey, readonly Subclass[]>> = {
  drilling: ['Development drills', 'Production drills'],
  loadhaul: ['Loaders', 'Haulage trucks'],
  surface: ['Top-hammer drills', 'Blasthole drills'],
};

/** Demo line 1842. `DIVNAME` — division key → the name stored on a machine. */
export const DIVNAME: Readonly<Record<DivisionKey, DivisionName>> = {
  drilling: 'Underground Drilling',
  loadhaul: 'Load & Haul',
  surface: 'Surface Drilling',
};

/** Demo line 1843. `DIVKEY` — the inverse of {@link DIVNAME}. */
export const DIVKEY: Readonly<Record<DivisionName, DivisionKey>> = {
  'Underground Drilling': 'drilling',
  'Load & Haul': 'loadhaul',
  'Surface Drilling': 'surface',
};

/**
 * Demo line 1845. `DIVLABEL` — what every screen reads.
 *
 * Deliberate mismatch, kept: the data stores `'Load & Haul'` but every screen
 * reads `'Underground Load & Haul'` (the demo's own comment at line 1844).
 */
export const DIVLABEL: Readonly<Record<DivisionKey, string>> = {
  drilling: 'Underground Drilling',
  loadhaul: 'Underground Load & Haul',
  surface: 'Surface Drilling',
};

/**
 * Demo line 1846. `divLabel` — accepts either a stored {@link DivisionName} or
 * a {@link DivisionKey}, and falls through to the input when neither matches.
 */
export function divLabel(division: string): string {
  const key = DIVKEY[division as DivisionName] ?? division;
  return DIVLABEL[key as DivisionKey] ?? division;
}

/** Demo line 1847. `CLSNAME` — short class names used on chips and legends. */
export const CLSNAME: Readonly<Record<MachineClass, string>> = {
  jumbo: 'Dev. jumbo',
  longhole: 'Production rig',
  lhd: 'Loader',
  truck: 'Truck',
  sdrill: 'Surface drill',
};

/** Demo line 1847. `CLS_ORDER`. */
export const CLS_ORDER = ['jumbo', 'longhole', 'lhd', 'truck', 'sdrill'] as const satisfies readonly MachineClass[];

/** Demo line 1848. `MINE_COL` — per-site colour token. */
export const MINE_COL: Readonly<Record<MineCode, string>> = {
  KG: 'var(--crit)',
  BR: 'var(--info)',
  MI: 'var(--good)',
  OH: 'var(--warn)',
  GD: 'var(--major)',
  MR: 'var(--accent)',
};

/** Demo line 2820. `MSHORT` — the short site names used on dense cards. */
export const MSHORT: Readonly<Record<MineCode, string>> = {
  KG: 'Kalbarri',
  BR: 'Broken Ridge',
  MI: 'Mt Isolde',
  OH: 'Ochre Hill',
  GD: 'Gawler Deep',
  MR: 'Mt Read',
};

/**
 * The fields {@link dataModeLabel}, {@link miningLabel} and {@link depthLabel}
 * read off a site.
 *
 * Declared structurally rather than as `Mine` so both the eager `MINES` record
 * and a `schema/mine.ts` `Mine` satisfy it — the two differ in `readonly`
 * modifiers on their array fields.
 */
export interface MineLabelFields {
  readonly id: MineCode;
  readonly data_mode: string;
  readonly mining: string;
  readonly depth_m: number;
}

/** Demo line 1849. `dataModeLabel`. */
export function dataModeLabel(mine: Pick<MineLabelFields, 'id' | 'data_mode' | 'mining'>): string {
  if (mine.data_mode !== 'Live') return 'Shift-end · Pitram';
  if (mine.mining === 'Open pit') return 'Live · LTE + RMS';
  if (mine.id === 'GD') return 'Live · LTE / Wi-Fi + RMS';
  return 'Live · Wi-Fi/LTE';
}

/**
 * Demo line 1851. `miningLabel` — the mine type, written the same way
 * everywhere (`Open pit`, `Underground`, `Open pit + underground`).
 */
export const miningLabel = (mine: Pick<MineLabelFields, 'mining'>): string => mine.mining;

/** Demo line 1852. `depthLabel`. */
export const depthLabel = (mine: Pick<MineLabelFields, 'mining' | 'depth_m'>): string =>
  `${miningLabel(mine)} · ${groupNum(mine.depth_m)} m`;

/** Demo line 1853. `healthOf` — the condition register rows for one machine. */
export const healthOf = (machineId: string): ComponentHealth[] =>
  HEALTH.filter((h) => h.machine_id === machineId);

/**
 * The demo's `h.band`, derived rather than stored.
 *
 * Demo line 1866 mutates every `HEALTH` row in place, setting `h.band` from
 * `band(h.health_score).k` and overwriting `h.status` with
 * `'critical' | 'watch' | 'ok'`. This port mutates nothing: `healthBand()` is
 * the derivation, and the register's own `status` column (which carries the
 * band, and agrees with this for all 1,068 rows) is left untouched.
 */
export const healthBand = (h: Pick<ComponentHealth, 'health_score'>): ConditionBand =>
  bandOf(h.health_score).key;

/**
 * Demo line 1866. The demo's `h.status` after its in-place rewrite:
 * `<40 critical`, `<60 watch`, otherwise `ok`. Kept as a pure function under a
 * distinct name so nothing shadows the register's stored `status`.
 */
export const healthAttention = (h: Pick<ComponentHealth, 'health_score'>): 'critical' | 'watch' | 'ok' =>
  h.health_score < 40 ? 'critical' : h.health_score < 60 ? 'watch' : 'ok';

/**
 * Demo line 1866. `STATUS` — a machine takes the band of its worst component.
 *
 * `Math.min()` of an empty list is `Infinity`, which matches no band and so
 * falls through `bandOf`'s `fair` fallback. Both quirks are the demo's and are
 * deliberately kept: a machine with no register rows reads `fair`, not `good`.
 */
export function statusOf(machineId: string): ConditionBand {
  const h = healthOf(machineId);
  const mn = Math.min(...h.map((x) => x.health_score));
  return bandOf(mn).key;
}

/** The band table, worst-first — the demo's `BANDS`, re-exported for legends. */
export const BANDS: readonly ConditionBandSpec[] = CONDITION_BANDS;

/** Demo line 2836 / 3151 order: the legend renders `BANDS` reversed (good first). */
export const bandsBestFirst = (): ConditionBandSpec[] => CONDITION_BANDS.slice().reverse();

/**
 * Demo line 1869. `INTERVAL` — change-out interval by component name, hours.
 *
 * `'Rock drill'` appears twice in the source literal (both `1000`); the
 * duplicate collapses here exactly as it does in the demo's object literal.
 */
export const INTERVAL: Readonly<Record<string, number>> = {
  'DTH hammer & pipes': 1200,
  'Rotary head': 4000,
  'Rock drill': 1000,
  'Rod changer': 3000,
  'Pipe carousel': 3000,
  'Dust collector': 1500,
  Compressor: 8000,
  'Tracks & undercarriage': 6000,
  'Levelling jacks': 6000,
  'Mast & feed': 8000,
  Boom: 8000,
  Feed: 5000,
  'Hydraulic pump & drive': 5000,
  'Retarder & service brakes': 1500,
  'RH rock drill': 1000,
  'LH rock drill': 1000,
  Tyres: 3000,
  Engine: 12000,
  'Bucket & GET': 800,
  'Battery thermal management': 4000,
  'Autonomy sensors & controller': 2000,
  'Remote control module & cameras': 3000,
};

/** Demo line 1870. `intervalOf` — 6,000 h is the demo's catch-all default. */
export const intervalOf = (component: string): number => INTERVAL[component] ?? 6000;

/**
 * Demo line 2174. `compRow` — one row of the component condition list, as data.
 */
export interface ComponentRow {
  readonly component: string;
  readonly score: number;
  readonly band: ConditionBandSpec;
  readonly hoursSinceChange: number;
  /** {@link intervalOf} for this component. */
  readonly interval: number;
  /** `interval - hours_since_change`; negative means overdue. */
  readonly remaining: number;
  readonly note: string | null;
}

/** Demo line 2174. `compRow`, data only. */
export function componentRow(c: ComponentHealth): ComponentRow {
  const interval = intervalOf(c.component);
  return {
    component: c.component,
    score: c.health_score,
    band: bandOf(c.health_score),
    hoursSinceChange: c.hours_since_change,
    interval,
    remaining: interval - c.hours_since_change,
    note: c.note,
  };
}

/**
 * Demo line 2177. `openMachine`'s component ordering: worst band first, then
 * lowest score. Ported as a non-mutating sort over a copy, as the demo does.
 */
export const machineComponents = (machineId: string): ComponentHealth[] =>
  healthOf(machineId)
    .slice()
    .sort(
      (a, b) =>
        CONDITION_BAND_ORDER[healthBand(a)] - CONDITION_BAND_ORDER[healthBand(b)] ||
        a.health_score - b.health_score,
    );

/** Demo line 2175 / 2181. The machine page's ◀ ▶ peer list: same subclass, fleet order. */
export const subclassPeers = (machineId: string): Machine[] => {
  const f = BY_ID[machineId];
  return FLEET.filter((x) => x.subclass === f.subclass);
};

/** Demo line 2181. One-based position of a machine among {@link subclassPeers}. */
export const subclassPosition = (machineId: string): number =>
  subclassPeers(machineId).findIndex((x) => x.machine_id === machineId) + 1;

/** Demo line 2175. `machineStep` — the wrapping ◀ ▶ neighbour, as a pure lookup. */
export function subclassStep(machineId: string, delta: number): string {
  const list = subclassPeers(machineId);
  const i = list.findIndex((x) => x.machine_id === machineId);
  return list[(i + delta + list.length) % list.length].machine_id;
}

// ===========================================================================
// Work orders
// ===========================================================================

/**
 * Demo line 1854. `wosOf` — one machine's work orders, newest first.
 *
 * `localeCompare` on an ISO timestamp is the demo's comparator; kept rather
 * than swapped for `<`/`>` so ties order identically.
 */
export const wosOf = (machineId: string, ctx: WorkOrderCtx): WorkOrder[] =>
  ctx.workOrders
    .filter((w) => w.machine_id === machineId)
    .sort((a, b) => b.created.localeCompare(a.created));

/** Demo line 2098 / 2109 / 2980. The agent-drafted work orders. */
export const draftWorkOrders = (ctx: WorkOrderCtx): WorkOrder[] =>
  ctx.workOrders.filter((w) => w.status.startsWith('Draft'));

/** Demo line 2942. Work orders released and waiting on parts. */
export const releasedWorkOrders = (ctx: WorkOrderCtx): WorkOrder[] =>
  ctx.workOrders.filter((w) => w.status.startsWith('Released'));

/** Demo line 2833. `pmDue` — preventive orders scheduled inside `days`. */
export const pmDue = (days: number, ctx: WorkOrderCtx): WorkOrder[] =>
  ctx.workOrders.filter(
    (w) =>
      w.wo_type === 'Preventive' &&
      w.status === 'Scheduled' &&
      w.created > OEM_TODAY &&
      daysTo(w.created.slice(0, 10)) <= days,
  );

/**
 * Demo line 2109. `renderApprovals` — the **data** half only.
 *
 * The demo's markup, its Approve buttons and its toast are the chrome lane's;
 * what is ported is the selection: every drafted work order, every
 * agent-touched parts order, and the one hard-coded restriction row.
 */
export interface ApprovalQueue {
  /** `WOS.filter(w => w.status.startsWith('Draft'))`. */
  readonly drafts: readonly WorkOrder[];
  /** `B.orders.filter(o => /agent/i.test(o.status))`. */
  readonly orders: readonly PartOrder[];
  /** The demo's literal third block — one static row, kept as authored. */
  readonly restriction: ApprovalRestriction;
}

/** The demo's hard-coded `RESTRICT-T-09` approval row (line 2109). */
export interface ApprovalRestriction {
  readonly id: string;
  readonly machine: string;
  readonly mine: MineCode;
  readonly draftedBy: string;
  readonly what: string;
  readonly action: OemAction;
}

/** Demo line 2109, verbatim — the restriction awaiting a planner. */
export const APPROVAL_RESTRICTION: ApprovalRestriction = {
  id: 'RESTRICT-T-09',
  machine: 'T-09',
  mine: 'MI',
  draftedBy: 'Safety & compliance agent',
  what: 'Restrict T-09 to empty haul until WO-26-11912 is closed; push to Pitram / FMS',
  action: act('machine', 'T-09'),
};

/** Demo line 2109. `renderApprovals`, data only. */
export const approvalQueue = (ctx: WorkOrderCtx & OrderCtx): ApprovalQueue => ({
  drafts: draftWorkOrders(ctx),
  orders: ctx.orders.filter((o) => /agent/i.test(o.status)),
  restriction: APPROVAL_RESTRICTION,
});

// ===========================================================================
// Availability and utilisation
// ===========================================================================

/** Demo line 1872. `availOf`'s default window start. */
export const AVAIL_FROM = '2026-08-31';
/** Demo line 1872. `availOf`'s default window end. */
export const AVAIL_TO = '2026-09-06';

/** Demo line 2552. `WK` — the reporting week every weekly figure is cut to. */
export const WK = {
  start: '2026-08-31',
  end: '2026-09-06',
  label: 'Mon 31 Aug – Sun 06 Sep 2026',
  short: 'week ending 06 Sep',
} as const;

/** Demo line 2553. `inWk`. */
export const inWk = (date: string): boolean => date >= WK.start && date <= WK.end;

/** Demo line 2554. `sum` — `+x[k] || 0` per row, so `null` reads as zero. */
const sumBy = <T,>(xs: readonly T[], k: keyof T): number =>
  xs.reduce((a, x) => a + (Number(x[k]) || 0), 0);

/**
 * Demo line 1872. `availOf` — availability from shift records, as a percentage.
 *
 * `hours ÷ (shifts × 2 × 9.5)`. The demo's own comment one line above says
 * "hours ÷ (shifts × 11.5)", which the code does not do; the code is what every
 * figure on screen was produced with, so `2 * 9.5` is what is ported. A shift
 * record row covers a **day** (both shifts), hence the `× 2`; 9.5 is the
 * scheduled operating hours per shift.
 *
 * Returns `null` — not `0` — when the machine has no rows in the window, and
 * every caller filters on `!= null` before averaging.
 */
export function availOf(
  machineId: string,
  ctx: ShiftCtx,
  from: string = AVAIL_FROM,
  to: string = AVAIL_TO,
): number | null {
  const s = ctx.shiftDaily.filter((x) => x.machine_id === machineId && x.date >= from && x.date <= to);
  return s.length ? +((s.reduce((a, x) => a + x.hours, 0) / (s.length * 2 * 9.5)) * 100).toFixed(1) : null;
}

/**
 * Demo line 3362. `avgAvail` — fleet mean availability.
 *
 * Note the divisor: the sum of the machines that **have** a figure, over
 * `FLEET.length` (100), not over the count that contributed. That is the
 * demo's arithmetic and it is what the headline percentage on the overview,
 * the control tower and the section pages all show; kept as written.
 */
export const avgAvail = (ctx: ShiftCtx): number =>
  +(
    FLEET.map((f) => availOf(f.machine_id, ctx))
      .filter((v): v is number => v != null)
      .reduce((a, b) => a + b, 0) / FLEET.length
  ).toFixed(1);

/** Demo line 3201. `siteAvail` — mean availability over one site's machines. */
export function siteAvail(mine: MineCode, ctx: ShiftCtx): number | null {
  const v = FLEET.filter((f) => f.mine === mine)
    .map((f) => availOf(f.machine_id, ctx))
    .filter((x): x is number => x != null);
  return v.length ? +(v.reduce((a, b) => a + b, 0) / v.length).toFixed(1) : null;
}

/** Demo line 3202. `siteCPH` — mean cost per operating hour over one site. */
export function siteCPH(mine: MineCode): number | null {
  const fs = FLEET.filter((f) => f.mine === mine);
  const c = COST_PER_MACHINE.filter((x) => fs.some((f) => f.machine_id === x.machine_id));
  return c.length ? Math.round(c.reduce((a, x) => a + x.cost_per_op_hour_aud, 0) / c.length) : null;
}

/**
 * Demo line 2144. `renderFleet`'s `byMine` — the site cost-per-operating-hour
 * figure on the fleet-health mine header.
 *
 * Different divisor from {@link siteCPH}: this one divides by the machine count
 * at the site, that one by the number of cost rows found. Both are the demo's;
 * both are kept, because both appear on screen.
 */
export const fleetCostPerOpHourByMine = (mine: MineCode): number =>
  +(
    COST_PER_MACHINE.filter((c) => BY_ID[c.machine_id].mine === mine).reduce(
      (a, c) => a + c.cost_per_op_hour_aud,
      0,
    ) / FLEET.filter((f) => f.mine === mine).length
  ).toFixed(0);

/** Demo line 2131. `machineCard` — the card's derived figures, no markup. */
export interface MachineCardData {
  readonly machine: Machine;
  readonly band: ConditionBand;
  /** Daily key-metric series for the last 12 days, in source order. */
  readonly values: readonly number[];
  readonly dates: readonly string[];
  /** The metric id on the first row, `''` when the machine has no rows. */
  readonly metric: string;
  readonly metricInfo: MetricInfo;
  /** Latest value in {@link MachineCardData.values}. */
  readonly latest: number | undefined;
  /** Worst attention-band component, or `null` when all are ≥ 60/100. */
  readonly worst: ComponentHealth | null;
  readonly availability: number | null;
  /** Band of the availability figure — the demo colours the number with it. */
  readonly availabilityBand: ConditionBandSpec | null;
  readonly action: OemAction;
}

/** Demo line 2131. `machineCard`, data only. */
export function machineCardData(
  machine: Machine,
  ctx: DailyMetricCtx & ShiftCtx,
): MachineCardData {
  const dm = ctx.dailyMetric.filter((d) => d.machine_id === machine.machine_id);
  const values = dm.map((d) => d.value);
  const metric = dm[0]?.metric ?? '';
  const worst =
    healthOf(machine.machine_id)
      .filter((h) => ATTENTION_BANDS.includes(healthBand(h)))
      .sort((a, b) => a.health_score - b.health_score)[0] ?? null;
  const av = availOf(machine.machine_id, ctx);
  return {
    machine,
    band: statusOf(machine.machine_id),
    values,
    dates: dm.map((d) => d.date),
    metric,
    metricInfo: metricInfoFor(metric),
    latest: values[values.length - 1],
    worst,
    availability: av,
    availabilityBand: av != null ? bandOf(av) : null,
    action: act('machine', machine.machine_id),
  };
}

/** Demo line 2144. One `renderFleet` mine header. */
export interface FleetMineSummary {
  readonly mine: MineCode;
  readonly machines: readonly Machine[];
  /** Machines in `major` or `vpoor`. */
  readonly veryPoorOrWorse: number;
  /** Machines in `poor`. */
  readonly poor: number;
  /** Machines in any {@link ATTENTION_BANDS} band — the jump-bar "to watch". */
  readonly toWatch: number;
  readonly costPerOpHour: number;
  readonly action: OemAction;
}

/** Demo line 2144. `renderFleet`'s per-mine header figures. */
export function fleetMineSummary(mine: MineCode): FleetMineSummary {
  const machines = FLEET.filter((f) => f.mine === mine);
  return {
    mine,
    machines,
    veryPoorOrWorse: machines.filter((f) => ['major', 'vpoor'].includes(statusOf(f.machine_id))).length,
    poor: machines.filter((f) => statusOf(f.machine_id) === 'poor').length,
    toWatch: machines.filter((f) => ATTENTION_BANDS.includes(statusOf(f.machine_id))).length,
    costPerOpHour: fleetCostPerOpHourByMine(mine),
    action: act('sites', mine),
  };
}

/** One division › subclass group inside a mine on the fleet-health page. */
export interface FleetGroup {
  readonly division: DivisionName;
  /** `divLabel(division)` — what the eyebrow reads. */
  readonly divisionLabel: string;
  readonly subclass: Subclass;
  readonly machines: readonly Machine[];
}

/**
 * Demo line 2144. The fleet page's grouping loop, including its fixed division
 * order — Surface Drilling first, then Underground Drilling, then Load & Haul.
 * Empty groups are dropped, as the demo's `if(!fs.length) continue` does.
 */
export function fleetGroups(mine: MineCode): FleetGroup[] {
  const order: readonly DivisionName[] = ['Surface Drilling', 'Underground Drilling', 'Load & Haul'];
  const out: FleetGroup[] = [];
  for (const division of order) {
    for (const subclass of SUBS[DIVKEY[division]]) {
      const machines = FLEET.filter((f) => f.mine === mine && f.subclass === subclass);
      if (!machines.length) continue;
      out.push({ division, divisionLabel: divLabel(division), subclass, machines });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// The reporting week — mineWeek
// ---------------------------------------------------------------------------

/** Demo line 2564. One machine's line in {@link MineWeek}. */
export interface MineWeekMachine {
  readonly machine_id: string;
  readonly model: ModelCode;
  readonly subclass: Subclass;
  readonly cls: MachineClass;
  readonly engine_hours: number;
  /** Band of the machine's **lowest-scoring** component. */
  readonly band: ConditionBand;
  readonly worst: string;
  readonly score: number;
  /** The register note, `''` where there is none (the demo's `w.note||''`). */
  readonly note: string;
  readonly avail: number | null;
  readonly hours: number;
  readonly alarms: number;
  readonly abuse: number;
  readonly breakdowns: number;
  /** Downtime on non-preventive orders raised in the week. */
  readonly down_h: number;
  /** Direct + lost-production cost of the week's orders, rounded. */
  readonly cost: number;
  readonly diesel: number;
  readonly kwh: number;
  readonly co2_t: number;
  readonly cph: number | null;
}

/** Demo line 2564. `mineWeek` — one site's reporting week. */
export interface MineWeek {
  readonly mine: MineCode;
  readonly name: string;
  readonly machines: readonly MineWeekMachine[];
  /** Machines on site. */
  readonly n: number;
  /** Shift-record rows in the window — machine-days, not calendar days. */
  readonly days: number;
  readonly avail: number;
  readonly breakdowns: number;
  /** Work orders raised in the week. */
  readonly wos: number;
  readonly reactive: number;
  readonly down_h: number;
  readonly cost: number;
  readonly diesel: number;
  readonly kwh: number;
  readonly co2_t: number;
  readonly bands: Readonly<Record<ConditionBand, number>>;
  readonly flagged: readonly MineWeekMachine[];
  readonly fair: readonly MineWeekMachine[];
  readonly drafts: readonly WorkOrder[];
  readonly late: number;
  readonly low: readonly InventoryLine[];
  readonly alerts: readonly Alert[];
  readonly pm_due: readonly WorkOrder[];
}

/**
 * Demo line 2564. `mineWeek` — every weekly figure for one site.
 *
 * Availability uses the same `shifts × 2 × 9.5` divisor as {@link availOf};
 * the demo's comment one line above says "engine h ÷ (shifts × 9.5 h)", and
 * again the code is what the reports were produced with.
 */
export function mineWeek(
  mine: MineCode,
  ctx: WorkOrderCtx & ShiftCtx & InventoryCtx,
): MineWeek {
  const fs = FLEET.filter((f) => f.mine === mine);
  const ids = new Set(fs.map((f) => f.machine_id));
  const sd = ctx.shiftDaily.filter((s) => ids.has(s.machine_id) && inWk(s.date));
  const ws = ctx.workOrders.filter((w) => w.mine === mine && inWk(w.created.slice(0, 10)));

  const rows: MineWeekMachine[] = fs.map((f) => {
    const id = f.machine_id;
    // Lowest score first — NOT the band-then-score order the machine page uses.
    const h = healthOf(id).slice().sort((a, b) => a.health_score - b.health_score);
    const w = h[0];
    const sm = sd.filter((s) => s.machine_id === id);
    const wm = ws.filter((x) => x.machine_id === id);
    const cp = COST_PER_MACHINE.find((c) => c.machine_id === id);
    return {
      machine_id: id,
      model: f.model,
      subclass: f.subclass,
      cls: f.cls,
      engine_hours: f.engine_hours,
      band: healthBand(w),
      worst: w.component,
      score: w.health_score,
      note: w.note ?? '',
      avail: sm.length ? +((sumBy(sm, 'hours') / (sm.length * 2 * 9.5)) * 100).toFixed(1) : null,
      hours: +sumBy(sm, 'hours').toFixed(1),
      alarms: sumBy(sm, 'alarms'),
      abuse: sumBy(sm, 'abuse'),
      breakdowns: wm.filter((x) => x.wo_type === 'Breakdown').length,
      down_h: +sumBy(
        wm.filter((x) => x.wo_type !== 'Preventive'),
        'downtime_h',
      ).toFixed(1),
      cost: Math.round(sumBy(wm, 'total_direct_cost_aud') + sumBy(wm, 'downtime_cost_aud')),
      diesel: Math.round(sumBy(sm, 'diesel_l')),
      kwh: Math.round(sumBy(sm, 'kwh')),
      co2_t: +(sumBy(sm, 'co2_kg') / 1000).toFixed(2),
      cph: cp ? cp.cost_per_op_hour_aud : null,
    };
  });
  rows.sort(
    (a, b) => CONDITION_BAND_ORDER[a.band] - CONDITION_BAND_ORDER[b.band] || a.score - b.score,
  );

  const bandKeys: readonly ConditionBand[] = ['good', 'fair', 'poor', 'vpoor', 'major'];
  const bands = Object.fromEntries(
    bandKeys.map((k) => [k, rows.filter((r) => r.band === k).length]),
  ) as Record<ConditionBand, number>;

  return {
    mine,
    name: MINES[mine].name,
    machines: rows,
    n: fs.length,
    days: sd.length,
    avail: +((sumBy(sd, 'hours') / (sd.length * 2 * 9.5)) * 100).toFixed(1),
    breakdowns: ws.filter((w) => w.wo_type === 'Breakdown').length,
    wos: ws.length,
    reactive: ws.filter((w) => w.wo_type !== 'Preventive').length,
    down_h: +sumBy(
      ws.filter((w) => w.wo_type !== 'Preventive'),
      'downtime_h',
    ).toFixed(1),
    cost: Math.round(sumBy(ws, 'total_direct_cost_aud') + sumBy(ws, 'downtime_cost_aud')),
    diesel: Math.round(sumBy(sd, 'diesel_l')),
    kwh: Math.round(sumBy(sd, 'kwh')),
    // One decimal at site level, two per machine — the demo's asymmetry, kept.
    co2_t: +(sumBy(sd, 'co2_kg') / 1000).toFixed(1),
    bands,
    flagged: rows.filter((r) => ATTENTION_BANDS.includes(r.band)),
    fair: rows.filter((r) => r.band === 'fair'),
    drafts: ctx.workOrders.filter((w) => w.mine === mine && w.status.startsWith('Draft')),
    late: ctx.workOrders.filter(
      (w) =>
        w.mine === mine &&
        w.wo_type === 'Preventive' &&
        w.status.includes('late') &&
        w.created >= '2026-08-01',
    ).length,
    low: ctx.inventory.filter(
      (i) => i.location === `${mine} site store` && i.qty_on_hand < i.min_level,
    ),
    alerts: ALERTS.filter((a) => BY_ID[a.machine].mine === mine),
    // Hard date, not `daysTo` — the demo's own literal for "the next fortnight".
    pm_due: ctx.workOrders
      .filter(
        (w) =>
          w.mine === mine &&
          w.wo_type === 'Preventive' &&
          w.status === 'Scheduled' &&
          w.created <= '2026-09-23',
      )
      .sort((a, b) => a.created.localeCompare(b.created))
      .slice(0, 8),
  };
}

// ===========================================================================
// Cost of inaction
// ===========================================================================

/** Demo line 3103. One row of `COI` — the planned/unplanned pair for an alert. */
export interface CoiInput {
  /** Planned labour hours. */
  readonly lab: number;
  /** Planned parts cost, AUD. */
  readonly partsP: number;
  /** Unplanned downtime hours. */
  readonly hU: number;
  /** Unplanned parts cost, AUD — the parts the failure takes with it. */
  readonly partsU: number;
  /** What the secondary damage is. */
  readonly sec: string;
  /** What the planned job looks like. */
  readonly pl: string;
}

/**
 * Demo line 3103. `COI`, verbatim.
 *
 * "What the job costs planned, against what the same failure costs unplanned —
 * downtime priced from the site rate card, plus the parts the failure takes
 * with it."
 */
export const COI: Readonly<Record<string, CoiInput>> = {
  'AL-0917': {
    lab: 5,
    partsP: 3200,
    hU: 14,
    partsU: 26500,
    sec: 'Disc set, hub seals and a recovery from the 1,450 decline; the truck is out for a night shift',
    pl: 'Pads at the 10:00 crib — no production lost, the truck is in the workshop window anyway',
  },
  'AL-0912': {
    lab: 6,
    partsP: 18900,
    hU: 18,
    partsU: 34000,
    sec: 'Front end destroyed rather than exchanged, shank and one boom hose with it; the cut stops',
    pl: 'Front-end change at the next cut change — the rig is already down for the change',
  },
  'AL-0915': {
    lab: 1.5,
    partsP: 480,
    hU: 6,
    partsU: 2100,
    sec: 'Derate becomes a shutdown on the 1,450 level; a tele-remote recovery through the ventilation cycle',
    pl: 'Air filter added to the PM-250 due 11 Sep — no extra downtime at all',
  },
  'AL-0919': {
    lab: 4,
    partsP: 21500,
    hU: 26,
    partsU: 96000,
    sec: 'One module replaced against a whole pack; the pack is the second most expensive part on the machine',
    pl: 'Module swap at the night-shift changeover when SO-90427 lands',
  },
  'AL-0925': {
    lab: 2.5,
    partsP: 4200,
    hU: 16,
    partsU: 9800,
    sec: 'Loader stranded below 40% state of charge on the 500 level, tow to the bay and the charger head checked too',
    pl: 'Connector change Thursday while Hamish is on site for the J-17 junction box',
  },
  'AL-0921': {
    lab: 4,
    partsP: 15800,
    hU: 9.2,
    partsU: 41000,
    sec: 'Hammer and pipes lost in the hole — the S-05 fishing job priced at what it actually cost on 05 Sep',
    pl: 'Exchange hammer at the pattern change Thursday — the rig is off the pattern regardless',
  },
  'AL-0923': {
    lab: 5,
    partsP: 28400,
    hU: 20,
    partsU: 62000,
    sec: 'Rotary head seizes on the string: head rebuild, table damage and a pattern left half drilled',
    pl: 'Motor exchange Thursday when SO-90431 lands, feed force backed off until then',
  },
};

/**
 * Demo line 3113. `lostRate` — lost production A$/h for a machine.
 *
 * Falls back to the site's loader rate, then to a flat A$1,000/h. Both
 * fallbacks are the demo's; sites carry `lost_prod` only for the classes they
 * run, so the `lhd` fallback fires on surface-only sites.
 */
export function lostRate(machineId: string): number {
  const f = BY_ID[machineId];
  const r = RATES[f.mine].lost;
  return r[f.cls] ?? r.lhd ?? 1000;
}

/** Demo line 3114. `coi` — the priced planned/unplanned comparison. */
export interface Coi {
  /** `lab × labour rate + planned parts`. */
  readonly planned: number;
  /** `downtime h × lost rate + unplanned parts + lab × 1.6 × labour rate`. */
  readonly unplanned: number;
  readonly delta: number;
  readonly hU: number;
  /** Lost production A$/h used — {@link lostRate}. */
  readonly lost: number;
  /** Site labour rate A$/h used. */
  readonly rate: number;
  readonly sec: string;
  readonly pl: string;
  readonly lab: number;
}

/**
 * Demo line 3114. `coi` — cost of inaction for one alert, or `null` when the
 * alert has no `COI` row.
 *
 * The `1.6` uplift on unplanned labour is the demo's magic number, kept.
 */
export function coi(alert: Pick<Alert, 'id' | 'machine'>): Coi | null {
  const c = COI[alert.id];
  if (!c) return null;
  const f = BY_ID[alert.machine];
  const rate = RATES[f.mine].labour;
  const lost = lostRate(alert.machine);
  const planned = c.lab * rate + c.partsP;
  const unplanned = c.hU * lost + c.partsU + c.lab * 1.6 * rate;
  return {
    planned,
    unplanned,
    delta: unplanned - planned,
    hU: c.hU,
    lost,
    rate,
    sec: c.sec,
    pl: c.pl,
    lab: c.lab,
  };
}

/** Demo line 3121. Total cost of waiting across the open alerts. */
export const totalCostOfInaction = (): number =>
  ALERTS.map((a) => coi(a))
    .filter((c): c is Coi => c != null)
    .reduce((s, c) => s + c.delta, 0);

/**
 * Demo line 3114. `INC_PLANNED` — what each incident should have cost if the
 * agent chain had caught it, hard-coded in the source.
 */
export const INC_PLANNED: Readonly<Record<string, number>> = {
  'WO-26-11842': 3900,
  'IR-2026-014': 4600,
  'IR-2026-015': 4100,
};

/** Demo line 3116. `newSince` — raised this morning. */
export const newSince = (alert: Pick<Alert, 'raised'>): boolean => alert.raised.startsWith('09 Sep');

// ===========================================================================
// Maximo and contracts
// ===========================================================================

/** Demo line 2825. `mxOf` — the Meridian Maximo record, or `null`. */
export const mxOf = (machineId: string, ctx: MaximoCtx): MaximoAsset | null =>
  ctx.maximo[machineId] ?? null;

/** Demo line 2826. `warrantyActive` — machines still inside factory warranty. */
export const warrantyActive = (ctx: MaximoCtx): Machine[] =>
  FLEET.filter((f) => mxOf(f.machine_id, ctx)?.warranty?.status === 'Active');

/** Who carries the cost of a job, and the tone the chip is drawn in. */
export interface PayerVerdict {
  /** e.g. `'Warranty'`, `'Contract labour · parts billable'`. */
  readonly label: string;
  /** Tone token: `'good' | 'info' | 'warn' | 'crit' | 'accent'`. */
  readonly tone: string;
}

/**
 * Demo line 2827. `whoPays` — the contract ladder, in the demo's exact order.
 *
 * No Maximo record at all means "Customer · billable"; the `Battery fleet care`
 * branch is reachable only for a machine whose `battery` flag is set.
 */
export function whoPays(machineId: string, ctx: MaximoCtx): PayerVerdict {
  const m = mxOf(machineId, ctx);
  if (!m) return { label: 'Customer · billable', tone: 'warn' };
  if (m.warranty.status === 'Active') return { label: 'Warranty', tone: 'good' };
  const t = m.servicecontract.type;
  if (/\+ parts/.test(t)) return { label: 'Service contract', tone: 'info' };
  if (/Battery fleet care/.test(t) && BY_ID[machineId].battery)
    return { label: 'Service contract (HV)', tone: 'info' };
  if (/Fleet Care|Drill Care/.test(t))
    return { label: 'Contract labour · parts billable', tone: 'info' };
  return { label: 'Customer · call-off', tone: 'warn' };
}

/**
 * Demo line 2829. `CONTRACT_EXC` — three machines whose commercial position is
 * decided by a live argument rather than by the contract ladder.
 */
export const CONTRACT_EXC: Readonly<Record<string, PayerVerdict>> = {
  'L-02': { label: 'Customer (warranty declined)', tone: 'crit' },
  'L-18': { label: 'Campaign · goodwill share', tone: 'accent' },
  'L-07': { label: 'Warranty claim under assessment', tone: 'warn' },
};

/** Demo line 2829. `payFor` — {@link CONTRACT_EXC} first, then {@link whoPays}. */
export const payFor = (machineId: string, ctx: MaximoCtx): PayerVerdict =>
  CONTRACT_EXC[machineId] ?? whoPays(machineId, ctx);

/**
 * Demo line 2830. `upgradeCandidates` — call-off machines in an attention band.
 *
 * The `!(f.battery && /Battery/.test(type))` clause excludes battery machines
 * already on a battery call-off, which the demo treats as a different
 * conversation. Kept verbatim.
 */
export function upgradeCandidates(ctx: MaximoCtx): Machine[] {
  return FLEET.filter((f) => {
    const m = mxOf(f.machine_id, ctx);
    if (!m) return false;
    const callOff =
      /call-off/.test(m.servicecontract.type) &&
      !(f.battery && /Battery/.test(m.servicecontract.type));
    return callOff && ATTENTION_BANDS.includes(statusOf(f.machine_id));
  });
}

/** Demo line 2831. A site contract with its days to renewal. */
export type ContractRenewal = MaximoContract & {
  /** Signed days from {@link OEM_TODAY} to `end`; negative means expired. */
  readonly days: number;
};

/** Demo line 2831. `renewals` — every site contract, soonest renewal first. */
export const renewals = (ctx: ContractCtx): ContractRenewal[] =>
  ctx.contracts.map((c) => ({ ...c, days: daysTo(c.end) })).sort((a, b) => a.days - b.days);

/** Demo line 2832. `pastInterval` — components run past their change interval. */
export const pastInterval = (): ComponentHealth[] =>
  HEALTH.filter((h) => h.hours_since_change > intervalOf(h.component));

/**
 * Demo line 2836. One row of `EXCHANGE` — the component exchange units in play.
 * The demo carries it as a 4-tuple; modelled here as a named shape.
 */
export interface ExchangeUnit {
  readonly part_no: string;
  readonly description: string;
  /** `'<machine> · <site>'`, as authored. */
  readonly where: string;
  readonly note: string;
}

/** Demo line 2836. `EXCHANGE`, verbatim. */
export const EXCHANGE: readonly ExchangeUnit[] = [
  {
    part_no: 'MER-DTH-HMR-165',
    description: 'DTH hammer, exchange unit',
    where: 'S-04 · Ochre Hill',
    note: 'reserved in the OH store · core back to Darwin after the fit',
  },
  {
    part_no: 'MER-ROT-HD-MOT',
    description: 'Rotary head motor, exchange',
    where: 'S-09 · Gawler Deep',
    note: '1 at Adelaide DC · SO-90431 drafted · core return on the same truck',
  },
  {
    part_no: 'MER-BAT-MOD',
    description: 'Battery module, exchange',
    where: 'L-07 · Broken Ridge',
    note: 'on air from Townsville · failed module to the HV workshop for teardown',
  },
  {
    part_no: 'MER-ENG-TRB-63',
    description: 'Turbocharger, exchange',
    where: 'L-18 · Gawler Deep',
    note: 'fitted 08 Sep · heat-damaged core in the Adelaide rebuild queue',
  },
];

/**
 * Demo line 2821. `FLEET_RULES` — the standing rules the close-outs produced.
 * Typed by `seed/workflow-steps.ts`, which reads it off the workflow context.
 */
export const FLEET_RULES: readonly FleetRule[] = [
  [
    'Breakout torque +10% over ten days → Watch; 9.5 kN·m → Act',
    'IR-2026-014',
    'MS-series strings',
  ],
  [
    'Hydraulic top-up above 5 L per shift → Watch',
    'IR-2026-015',
    'every loader, owner and contractor',
  ],
  [
    'Contract PM interval longer than the OEM interval → flag per machine',
    'IR-2026-015',
    'all contractor plans',
  ],
  [
    'Overdue oil sample + temperature rise → Watch; 250 h sampling on ML-21',
    'WO-26-11842',
    'ML-21 series',
  ],
  ['Brake/throttle overlap on the operator scorecard', 'RMS-MI-2026-0231', 'MT-series on declines'],
  [
    'Charging connector off the wash-down route; insulation < 100 MΩ → change',
    'AL-0925',
    'ML-21E',
  ],
];

/** Demo line 2837. `revenue` — the aftermarket lens, OEM side. */
export interface Revenue {
  /** Parts at list on every work order raised in the last 12 months. */
  readonly parts12: number;
  /** The same over the whole 18-month ledger. */
  readonly parts18: number;
  /** Meridian technician labour on completed jobs, excluding travel, 10 weeks. */
  readonly labour10w: number;
  readonly freightAll: number;
  /** Freight on breakdown orders only. */
  readonly freightBd: number;
  /** Breakdown + corrective parts spend ÷ `parts18`. */
  readonly reactiveShare: number;
}

/**
 * Demo line 2837. `revenue`.
 *
 * `cut = '2025-09-09'` is the demo's literal 12-month boundary — a hard date,
 * not derived from the clock, and kept as such. The site labour rate falls back
 * to A$150/h (`|| 150`), which never fires on the shipped rate card.
 */
export function revenue(ctx: WorkOrderCtx & TechJobCtx): Revenue {
  const cut = '2025-09-09';
  const r12 = ctx.workOrders.filter((w) => w.created >= cut);
  const parts = r12.reduce((a, w) => a + (w.parts_cost_aud || 0), 0);
  const parts18 = ctx.workOrders.reduce((a, w) => a + (w.parts_cost_aud || 0), 0);
  const lab = ctx.techJobs
    .filter((j) => j.status === 'Completed')
    .reduce((a, j) => {
      const f = BY_ID[j.machine];
      const h = j.labour_lines
        .filter((l) => !/Travel/i.test(l.activity))
        .reduce((x, l) => x + l.hours, 0);
      return a + h * (RATES[f.mine].labour || 150);
    }, 0);
  const frAll = ctx.workOrders.reduce((a, w) => a + (w.freight_cost_aud || 0), 0);
  const frBd = ctx.workOrders
    .filter((w) => w.wo_type === 'Breakdown')
    .reduce((a, w) => a + (w.freight_cost_aud || 0), 0);
  const reactive = ctx.workOrders
    .filter((w) => w.wo_type === 'Breakdown' || w.wo_type === 'Corrective')
    .reduce((a, w) => a + (w.parts_cost_aud || 0), 0);
  return {
    parts12: parts,
    parts18,
    labour10w: lab,
    freightAll: frAll,
    freightBd: frBd,
    reactiveShare: reactive / parts18,
  };
}

// ===========================================================================
// Technicians
// ===========================================================================

/** Demo line 2711. `CLS_SKILL` — the skills a machine class calls for. */
export const CLS_SKILL: Readonly<Record<MachineClass, readonly string[]>> = {
  sdrill: ['DTH', 'top-hammer', 'rotary', 'compressor', 'undercarriage'],
  lhd: ['hydraulics', 'HV', 'driveline', 'engine'],
  truck: ['brakes', 'driveline', 'engine', 'retarder'],
  jumbo: ['rock drill', 'boom', 'HL-series'],
  longhole: ['rock drill', 'autonomy', 'HL-series'],
};

/** Demo line 2712. `techStats` — one technician's measured performance. */
export interface TechStats {
  /** Technician-app jobs assigned, all statuses. */
  readonly jobs: number;
  readonly done: number;
  readonly open: readonly TechnicianJob[];
  /** Mean CSS `Overall`, or `null` when no survey has come back. */
  readonly cssAvg: number | null;
  readonly cssN: number;
  /** Fix-first-time %, or `null` when nothing is completed. */
  readonly fft: number | null;
  /** Utilisation %, capped at 100; `0` when every rostered day is a day off. */
  readonly util: number;
  /** Hours booked in the week, job + PM support + travel. */
  readonly hours: number;
  readonly lms: readonly TrainingRecord[];
  readonly expiring: readonly TrainingRecord[];
}

/**
 * Demo line 2712. `techStats`.
 *
 * Fix-first-time counts a completed job as a repeat when another
 * **non-preventive** order exists on the same machine **and the same
 * component** within 60 days after the closed order was raised. The 60-day
 * window (`60 * 864e5`) and the 12-hour utilisation day are the demo's
 * constants, kept.
 *
 * Timestamps go through `Date.parse`, not `new Date(x).getTime()` — the same
 * value, spelled so nothing in this module ever constructs a `Date`.
 */
export function techStats(
  name: string,
  ctx: TechJobCtx & CssCtx & WorkOrderCtx & TimesheetCtx & TrainingCtx,
): TechStats {
  const jobs = ctx.techJobs.filter((j) => j.technician === name);
  const done = jobs.filter((j) => j.status === 'Completed');
  const css = ctx.css.filter((r) => r.Technician === name);
  const cssAvg = css.length ? css.reduce((a, r) => a + r.Overall, 0) / css.length : null;

  const repeat = done.filter((j) => {
    const w = ctx.workOrders.find((x) => x.wo_id === j.wo_ref);
    if (!w) return false;
    const t0 = Date.parse(w.created);
    return ctx.workOrders.some(
      (x) =>
        x.machine_id === w.machine_id &&
        x.component === w.component &&
        x.wo_id !== w.wo_id &&
        x.wo_type !== 'Preventive' &&
        Date.parse(x.created) > t0 &&
        Date.parse(x.created) <= t0 + 60 * 864e5,
    );
  }).length;

  const fft = done.length ? Math.round((1 - repeat / done.length) * 100) : null;
  const ts = ctx.timesheets.filter((t) => t.technician === name);
  const worked = ts.filter((t) => !t.day_off);
  const util = worked.length
    ? Math.min(
        100,
        Math.round(
          (worked.reduce((a, t) => a + t.job_hours + t.pm_support_hours, 0) / (worked.length * 12)) *
            100,
        ),
      )
    : 0;
  const lms = ctx.training.filter((r) => r.person === name);

  return {
    jobs: jobs.length,
    done: done.length,
    open: jobs.filter((j) => j.status !== 'Completed'),
    cssAvg,
    cssN: css.length,
    fft,
    util,
    hours: +worked
      .reduce((a, t) => a + t.job_hours + t.pm_support_hours + t.travel_hours, 0)
      .toFixed(1),
    lms,
    expiring: lms.filter((r) => r.status !== 'Current'),
  };
}

/** Demo line 2834. `meanUtil` — mean utilisation across the technician roster. */
export function meanUtil(
  ctx: TechPresenceCtx & TechJobCtx & CssCtx & WorkOrderCtx & TimesheetCtx & TrainingCtx,
): number {
  const u = ctx.techs.map((t) => techStats(t.technician, ctx).util);
  return Math.round(u.reduce((a, b) => a + b, 0) / u.length);
}

/** Demo line 2717. One open job the dispatcher has to place. */
export interface DispatchOpenJob {
  /** Technician-app job id or work-order id. */
  readonly ref: string;
  readonly wo: string;
  readonly machine: string;
  /** Site **display name**, not the mine code. */
  readonly site: string;
  /** Mine code, or `undefined` when the site name is not one of the six. */
  readonly mine: MineCode | undefined;
  readonly desc: string;
  /**
   * Technician already on the job.
   *
   * `undefined` on every drafted work order: the demo reads `w.technician`,
   * which work orders do not carry. Kept as written — it means the
   * "already assigned" +1 never fires for a draft.
   */
  readonly tech: string | undefined;
}

/** Demo line 2717. `dispatchProposals` — one agent-proposed assignment. */
export interface DispatchProposal extends DispatchOpenJob {
  /** Highest-scoring technician. */
  readonly best: string;
  /** The reasons behind the score, `' · '` joined, as the demo prints them. */
  readonly why: string;
  /** Runner-up, `''` when there is only one candidate. */
  readonly alt: string;
}

/**
 * Demo line 2717. `dispatchProposals`.
 *
 * The scoring is the demo's, verbatim: +3 based at the site, +2 on site or −3
 * off roster, +2 for a matching skill, −1 at three or more open jobs, +1
 * already assigned, +1 standing customer preference. Highest score wins; ties
 * keep source order because `Array.prototype.sort` is stable.
 *
 * One guard added: the customer-preference clause is skipped when the job's
 * site name did not resolve to a mine code. The demo would throw there; no
 * shipped row reaches it.
 */
export function dispatchProposals(
  ctx: TechJobCtx &
    WorkOrderCtx &
    TechPresenceCtx &
    RosterCtx &
    CssCtx &
    TimesheetCtx &
    TrainingCtx,
): DispatchProposal[] {
  const open: DispatchOpenJob[] = [
    ...ctx.techJobs
      .filter((j) => j.status !== 'Completed')
      .map((j) => ({
        ref: j.job_id,
        wo: j.wo_ref,
        machine: j.machine,
        site: j.site,
        mine: MINE_ORDER.find((m) => MINES[m].name === j.site),
        desc: j.findings,
        tech: j.technician as string | undefined,
      })),
    ...ctx.workOrders
      .filter((w) => w.status.startsWith('Draft'))
      .map((w) => ({
        ref: w.wo_id,
        wo: w.wo_id,
        machine: w.machine_id,
        site: MINES[w.mine].name,
        mine: w.mine as MineCode | undefined,
        desc: w.description,
        // The demo's `w.technician` — not a work-order field. See the doc above.
        tech: undefined,
      })),
  ];

  return open.map((o) => {
    const f = BY_ID[o.machine];
    const need = CLS_SKILL[f.cls] ?? [];
    const cands = ctx.techs
      .map((t) => {
        let sc = 0;
        const why: string[] = [];
        if (t.base === o.mine) {
          sc += 3;
          why.push('based at the site');
        }
        if (t.status === 'On site') {
          sc += 2;
        } else {
          sc -= 3;
          why.push('off roster from 10 Sep');
        }
        const sk = t.skills.filter((s) =>
          need.some((n) => s.toLowerCase().includes(n.toLowerCase())),
        );
        if (sk.length) {
          sc += 2;
          why.push(sk[0]);
        }
        const st = techStats(t.technician, ctx);
        if (st.open.length >= 3) {
          sc -= 1;
          why.push(`${st.open.length} open jobs`);
        }
        if (t.technician === o.tech) {
          sc += 1;
          why.push('already assigned');
        }
        const pref = ctx.roster.find((r) => r.Technician === t.technician);
        const wanted = pref?.['Customer preference'];
        if (
          wanted &&
          o.mine &&
          wanted.toLowerCase().includes(MINES[o.mine].name.split(' ')[0].toLowerCase())
        ) {
          sc += 1;
          why.push('customer preference');
        }
        return { t, sc, why };
      })
      .sort((a, b) => b.sc - a.sc);
    const best = cands[0];
    return { ...o, best: best.t.technician, why: best.why.join(' · '), alt: cands[1]?.t.technician ?? '' };
  });
}

/** Demo line 3196. `siteTechs` — Meridian technicians based at a site. */
export const siteTechs = (mine: MineCode, ctx: TechPresenceCtx): TechnicianPresence[] =>
  ctx.techs.filter((t) => t.base === mine);

// ===========================================================================
// Parts and inventory
// ===========================================================================

/** Demo line 2762. `causesFor` — one ranked failure cause. */
export interface FailureCauseRow {
  readonly cause: string;
  /** Work orders carrying this cause on this component. */
  readonly n: number;
  /** `n` as a percentage of the component's corrective history, rounded. */
  readonly pct: number;
  /** The description of the first order with this cause, `''` when none. */
  readonly eg: string;
}

/**
 * Demo line 2762. `causesFor` — the top five causes for a component, ranked
 * across the whole fleet's non-preventive work orders.
 *
 * `tot = ws.length || 1` keeps the division safe when no order matched; the
 * result is then an empty list anyway. Kept as written.
 */
export function causesFor(component: string, ctx: WorkOrderCtx): FailureCauseRow[] {
  const ws = ctx.workOrders.filter(
    (w) => w.component === component && w.wo_type !== 'Preventive' && w.failure_cause,
  );
  const by: Record<string, number> = {};
  ws.forEach((w) => {
    const c = w.failure_cause as string;
    by[c] = (by[c] ?? 0) + 1;
  });
  const tot = ws.length || 1;
  return Object.entries(by)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([c, n]) => ({
      cause: c,
      n,
      pct: Math.round((n / tot) * 100),
      eg: ws.find((w) => w.failure_cause === c)?.description ?? '',
    }));
}

/**
 * Demo line 2763. `procFor` — the controlled procedure for a component.
 *
 * The demo declares `procFor(comp, model)` but never reads `model`; the
 * parameter is dropped here rather than carried as dead weight.
 *
 * Returns the `PM-MER…` general checklist when no specific procedure matches,
 * and `undefined` if even that is absent from the library.
 */
export function procFor(component: string, ctx: DocumentCtx): LibraryDocument | undefined {
  const key = /brake|retarder/i.test(component)
    ? 'SP-MT63-BRK'
    : /hydraulic pump/i.test(component)
      ? 'SP-ML21-HYD'
      : /rock drill/i.test(component)
        ? 'SP-MJ2D-RD'
        : /dth|pipe|hammer/i.test(component)
          ? 'SP-MS120-DTH'
          : /rotary|carousel|mast/i.test(component)
            ? 'SP-MS-PIPE'
            : /battery|hv|charging/i.test(component)
              ? 'LOTO-UG-003'
              : null;
  return key
    ? ctx.documents.find((d) => d.file.startsWith(key))
    : ctx.documents.find((d) => d.file.startsWith('PM-MER'));
}

/**
 * Demo line 2784. `PART_OF_COMP` — which part numbers a component consumes.
 * Ordered: {@link predictedUsage} takes the **first** matching pattern.
 */
export const PART_OF_COMP: readonly (readonly [RegExp, readonly string[]])[] = [
  [/dth|pipe|hammer/i, ['MER-DTH-HMR-165', 'MER-DTH-PIPE-6M']],
  [/rotary/i, ['MER-ROT-HD-MOT', 'MER-ROT-HD-SK']],
  [/retarder|brake/i, ['MER-BRK-PAD-63']],
  [/rock drill/i, ['MER-RD-FE-KIT', 'MER-RD-SHK-T45']],
  [/hydraulic pump & drive/i, ['MER-HYD-CPL-210', 'MER-HYD-SK-210']],
  [/^engine$/i, ['MER-ENG-TRB-63']],
  [/transmission/i, ['MER-TRN-CLT-K']],
  [/rear axle/i, ['MER-AXL-BRK-K']],
  [/battery pack/i, ['MER-BAT-MOD']],
  [/cable reel/i, ['MER-CBL-REEL']],
  [/carousel|rod changer/i, ['MER-ROD-CAR-K']],
  [/dust/i, ['MER-DC-FLT-K']],
  [/track/i, ['MER-TRK-PAD-K', 'MER-TRK-ROL']],
  [/tyres/i, ['MER-TYR-2625']],
  [/bucket/i, ['MER-GET-TIP']],
  [/power pack/i, ['MER-HYD-PMP-JB']],
  [/compressor/i, ['MER-CMP-SEP']],
];

/** Demo line 2785. `predictedUsage` — one predicted part/site demand row. */
export interface PredictedUsageRow {
  readonly part_no: string;
  readonly mine: MineCode;
  /** Weighted demand: 1 per `major` or `vpoor` component, 0.5 per `poor`. */
  readonly demand: number;
  /** Machines that contributed, in register order; ids repeat per part. */
  readonly machines: readonly string[];
  readonly desc: string;
  /** Catalogue criticality, `''` when the part is not in the catalogue. */
  readonly crit: string;
  /** Quantity in the site store. */
  readonly store: number;
  /** Site-store minimum, or `'—'` when the store carries no line. */
  readonly min: number | '—';
  /** Quantity at the serving distribution centre. */
  readonly dcq: number;
  /** `ceil(demand) - store`; ≤ 0 means covered. */
  readonly gap: number;
  /** The recommendation sentence, formatted as the demo writes it. */
  readonly prop: string;
  /** Suggested new site minimum, at least 1. */
  readonly newmin: number;
}

/**
 * Demo line 2785. `predictedUsage`.
 *
 * `major` and `vpoor` both weight 1 and `poor` weights 0.5 — the demo writes
 * the two 1s out separately (`h.band==='major'?1:h.band==='vpoor'?1:0.5`), so
 * the ladder is kept rather than collapsed.
 *
 * Sorted by gap descending, then demand descending.
 */
export function predictedUsage(ctx: PartsCatalogueCtx & InventoryCtx): PredictedUsageRow[] {
  const rows: Record<string, { part_no: string; mine: MineCode; demand: number; machines: string[] }> =
    {};
  HEALTH.filter((h) => ATTENTION_BANDS.includes(healthBand(h))).forEach((h) => {
    const m = PART_OF_COMP.find((p) => p[0].test(h.component));
    if (!m) return;
    const f = BY_ID[h.machine_id];
    const band = healthBand(h);
    const w = band === 'major' ? 1 : band === 'vpoor' ? 1 : 0.5;
    m[1].forEach((pn) => {
      const k = `${pn}|${f.mine}`;
      rows[k] = rows[k] ?? { part_no: pn, mine: f.mine, demand: 0, machines: [] };
      rows[k].demand += w;
      rows[k].machines.push(h.machine_id);
    });
  });

  return Object.values(rows)
    .map((r): PredictedUsageRow => {
      const cat = ctx.parts.find((p) => p.part_no === r.part_no);
      const st = ctx.inventory.find(
        (i) => i.location === `${r.mine} site store` && i.part_no === r.part_no,
      );
      const dc = ctx.inventory.find(
        (i) => i.location === RATES[r.mine].dc && i.part_no === r.part_no,
      );
      const store = st ? st.qty_on_hand : 0;
      const dcq = dc ? dc.qty_on_hand : 0;
      const gap = Math.ceil(r.demand) - store;
      const prop =
        gap <= 0
          ? 'Covered by site store'
          : dcq >= gap
            ? `Transfer ${gap} from ${RATES[r.mine].dc} (${RATES[r.mine].freight_days} d road)`
            : `Order ${gap} — DC holds ${dcq}; lead time ${cat?.lead_time_days ?? '—'} d`;
      return {
        ...r,
        desc: cat?.description ?? '',
        crit: cat?.criticality ?? '',
        store,
        min: st ? st.min_level : '—',
        dcq,
        gap,
        prop,
        newmin: Math.max(1, Math.ceil(r.demand)),
      };
    })
    .sort((a, b) => b.gap - a.gap || b.demand - a.demand);
}

/** Demo line 3198. `siteStore` — the site store's inventory lines. */
export const siteStore = (mine: MineCode, ctx: InventoryCtx): InventoryLine[] =>
  ctx.inventory.filter((i) => i.location === `${mine} site store`);

/** Demo line 3199. `dcStore` — the serving distribution centre's lines. */
export const dcStore = (mine: MineCode, ctx: InventoryCtx): InventoryLine[] =>
  ctx.inventory.filter((i) => i.location === RATES[mine].dc);

/** Demo line 2098 / 2967. Site-store lines below their minimum, fleet-wide. */
export const siteLinesBelowMin = (ctx: InventoryCtx): InventoryLine[] =>
  ctx.inventory.filter((i) => i.qty_on_hand < i.min_level && i.location.includes('site'));

// ===========================================================================
// Jobs and promises
// ===========================================================================

/** Demo line 2942. One row of `WIN` — the executable date behind an alert. */
export interface ServiceWindow {
  /** Earliest days-to-fix the evidence supports. */
  readonly min: number;
  /** Latest days-to-fix before the failure is expected. */
  readonly max: number;
  /** How the window is described on screen. */
  readonly label: string;
  /** The committed fix, in words. */
  readonly fix: string;
  /** The committed fix as days from now — compared against `max`. */
  readonly fixd: number;
  /** Where the parts are. */
  readonly parts: string;
  /** Who does it. */
  readonly tech: string;
  /** The production window it fits inside. */
  readonly window: string;
}

/** Demo line 2942. `WIN`, verbatim — service promises keyed by alert id. */
export const WIN: Readonly<Record<string, ServiceWindow>> = {
  'AL-0917': {
    min: 0,
    max: 0.3,
    label: 'inside the shift',
    fix: '10:00 crib today',
    fixd: 0.16,
    parts: '3 pad sets in the MI store · 1 reserved',
    tech: 'Anders Lindqvist · on site',
    window: 'Redgum: crib 10:00–11:00',
  },
  'AL-0912': {
    min: 4,
    max: 7,
    label: '60–110 percussion h · 4–7 days',
    fix: 'cut change Fri 11 Sep',
    fixd: 2.5,
    parts: 'kit on air from Perth · ETA 11 Sep',
    tech: 'Ben Tuilagi (HL-series) · back on swing 11 Sep',
    window: 'Broken Ridge: next cut change',
  },
  'AL-0915': {
    min: 2,
    max: 6,
    label: 'watch',
    fix: 'PM-250 Fri 11 Sep',
    fixd: 2,
    parts: 'air filter in the MI store (engine service kit)',
    tech: 'Redgum crew with the PM',
    window: 'Redgum: PM-250 slot',
  },
  'AL-0919': {
    min: 0,
    max: 1,
    label: 'derated · fast charge locked out',
    fix: 'module lands tonight (SO-90427)',
    fixd: 0.5,
    parts: 'MER-BAT-MOD on air from Townsville · ETA 09 Sep PM',
    tech: 'Ben Tuilagi HV-current',
    window: 'Broken Ridge: night shift changeover',
  },
  'AL-0925': {
    min: 1,
    max: 2.5,
    label: '3–5 charge cycles',
    fix: 'Thu 10 Sep · Hamish carries the connector',
    fixd: 1,
    parts: 'MER-CHG-INT from Burnie · SO-90435 drafted',
    tech: 'Hamish Cameron · only HV-authorised Meridian tech in TAS',
    window: 'Western Tiers: day shift, bay 1 kept free',
  },
  'AL-0921': {
    min: 3,
    max: 5,
    label: '40–70 drilling h',
    fix: 'pattern change Thu 10 Sep',
    fixd: 1,
    parts: 'exchange hammer reserved in the OH store',
    tech: 'Kieran Walsh · only DTH-certified in NT',
    window: 'Ochre Hill: pattern change',
  },
  'AL-0923': {
    min: 1,
    max: 3,
    label: 'reduce feed force now',
    fix: 'motor lands Thu 10 Sep (SO-90431)',
    fixd: 1,
    parts: 'MER-ROT-HD-MOT from Adelaide DC · road 2 d',
    tech: 'Noah Petrov · on site',
    window: 'Gawler Deep: pre-split row until then',
  },
};

/** Demo line 3117. `windowPct` — how much of an alert's window is left. */
export interface WindowState {
  /** Percentage of the window remaining, 0–100. */
  readonly pct: number;
  readonly label: string;
  /** The committed fix; absent on the no-`WIN` fallback. */
  readonly fix?: string;
  /** `true` when the whole window is a day or less. */
  readonly urgent: boolean;
  /** The window's `max`; absent on the fallback. */
  readonly left?: number;
}

/**
 * Demo line 3117. `windowPct`.
 *
 * `span = Math.max(w.max, 0.2)` floors the denominator so the inside-the-shift
 * alert (`max: 0.3`) does not divide by something near zero. The fallback for
 * an alert with no `WIN` row is a flat 50% "under review" — the demo's, kept.
 */
export function windowPct(alert: Pick<Alert, 'id'>): WindowState {
  const w = WIN[alert.id];
  if (!w) return { pct: 50, label: 'under review', urgent: false };
  const span = Math.max(w.max, 0.2);
  const used = Math.max(0, Math.min(1, (span - w.fixd) / span));
  return { pct: Math.round((1 - used) * 100), label: w.label, fix: w.fix, urgent: w.max <= 1, left: w.max };
}

/** Demo line 2943. `promises` — an alert with its executable service promise. */
export interface ServicePromise {
  readonly id: string;
  readonly machine: string;
  readonly mine: MineCode;
  readonly title: string;
  readonly win: ServiceWindow;
  /** `fixd <= max` — the committed fix lands inside the window. */
  readonly inside: boolean;
  readonly pay: PayerVerdict;
}

/**
 * Demo line 2943. `promises` — every alert that has a `WIN` row, with who pays.
 * Alerts with no row are dropped (the demo's `.filter(Boolean)`).
 */
export const promises = (ctx: MaximoCtx): ServicePromise[] =>
  ALERTS.map((a): ServicePromise | null => {
    const w = WIN[a.id];
    if (!w) return null;
    return {
      id: a.id,
      machine: a.machine,
      mine: BY_ID[a.machine].mine,
      title: a.title,
      win: w,
      inside: w.fixd <= w.max,
      pay: payFor(a.machine, ctx),
    };
  }).filter((p): p is ServicePromise => p != null);

// ---------------------------------------------------------------------------
// buildJobs — every open job on the loop, at the module it has reached
// ---------------------------------------------------------------------------

/** Demo line 2945. The loop module a job has reached. */
export type JobStage =
  | 'demand'
  | 'diagnose'
  | 'promise'
  | 'parts'
  | 'execute'
  | 'closeout'
  | 'orchestrate'
  | 'commercial'
  | 'capacity'
  | 'installed';

/** Demo line 2945. Severity tone on a job row. */
export type JobSeverity = 'crit' | 'warn' | 'info' | 'done';

/**
 * Demo line 2945. One job on the loop.
 *
 * The demo carried an `open()` closure on every row. Here that is a typed
 * {@link OemAction}; the routing lane resolves it. The `audit(…)` rows — the
 * demo's `audit('cases',{case_no})` — become `act('audit', 'cases:<case_no>')`
 * so the dataset and its key survive inside `{ view, sub }`.
 */
export interface OemJob {
  /** Alert / incident / work-order / case / job id, or `'<machine> · <component>'`. */
  readonly id: string;
  /** What kind of thing it is, as printed. */
  readonly kind: string;
  readonly title: string;
  /** Machine id, or `null` on the fleet-level rows (onboarding, capacity, …). */
  readonly machine: string | null;
  readonly mine: MineCode;
  readonly stage: JobStage;
  /** Progress through the loop, 0–100. Hard-coded per row in the demo. */
  readonly pct: number;
  /** What happens next, and who has it. */
  readonly next: string;
  readonly owner: string;
  readonly sev: JobSeverity;
  /** Where the row opens. */
  readonly action: OemAction;
  /** The worked-example run this row belongs to, or `null`. */
  readonly run: string | null;
}

/** The demo's `AS` table (line 2946): alert id → [stage, pct, next, owner]. */
const JOB_ALERT_STATE: Readonly<Record<string, readonly [JobStage, number, string, string]>> = {
  'AL-0917': [
    'promise',
    45,
    'Planner to approve WO-26-11912 and the empty-haul restriction',
    'Jorge Castillo',
  ],
  'AL-0912': [
    'parts',
    62,
    'Front-end kit on air from Perth, ETA 11 Sep; fit at the cut change',
    'Tom Reilly',
  ],
  'AL-0915': [
    'diagnose',
    30,
    'Air filter check at PM-250 (11 Sep); ventilation cycle with Owen Fraser',
    'Anders Lindqvist',
  ],
  'AL-0919': [
    'parts',
    68,
    'Module lands 09 Sep PM (SO-90427); HV isolation test after fit',
    'Ben Tuilagi',
  ],
  'AL-0921': ['promise', 50, 'Approve WO-26-11913; hammer reserved in the OH store', 'Mei Lin Tan'],
  'AL-0923': [
    'promise',
    46,
    'Approve WO-26-11914 and SO-90431 (motor from Adelaide)',
    'Callum Byrne',
  ],
  'AL-0925': [
    'promise',
    40,
    'Approve WO-26-11917 and SO-90435; Hamish carries the connector Thursday',
    'Isla Mackenzie',
  ],
};

/** The demo's `IS` table (line 2948): incident id → [pct, next, owner]. */
const JOB_INCIDENT_STATE: Readonly<Record<string, readonly [number, string, string]>> = {
  'WO-26-11842': [
    90,
    'Follow-up oil sample at +50 h (≈ 11 Sep); Kalbarri on the pilot at L1',
    'Alicia Fenton',
  ],
  'IR-2026-014': [
    85,
    'Thread-gauge at every PM-250 from Monday; torque briefing Tuesday',
    'Mei Lin Tan',
  ],
  'IR-2026-015': [
    80,
    'Hose campaign across the ML-21 fleet (L-17 first); contract PM plan aligned by Friday',
    'Callum Byrne',
  ],
};

/** The demo's alert → worked-example run map (line 2947). */
const JOB_ALERT_RUN: Readonly<Record<string, string>> = {
  'AL-0912': 'predictive',
  'AL-0917': 'seismic',
};

/** Demo line 2954/2959. `MINES[m].planner` / `maint_super` minus its parenthetical. */
const nameOnly = (s: string): string => s.split(' (')[0];

/**
 * Demo line 2945. `buildJobs` — every open job on the loop, in demo order.
 *
 * The order and the de-duplication are load-bearing and are kept exactly:
 * alerts, then incidents, then a `seen` set of their machines; then
 * draft/released work orders (one per unseen machine); then in-progress
 * technician jobs (skipped when a job with the same work-order ref is already
 * on the list — note it tests the **list**, not `seen`); then open technical and
 * parts cases and every warranty case (both add to `seen` unconditionally);
 * then `poor` components on machines not yet seen; then the seven fixed
 * onboarding, capacity, renewal and upgrade rows.
 */
export function buildJobs(ctx: WorkOrderCtx & TechJobCtx & CaseCtx): OemJob[] {
  const J: OemJob[] = [];
  const add = (o: OemJob): number => J.push(o);

  ALERTS.forEach((a) => {
    const [st, p, nx, own] = JOB_ALERT_STATE[a.id] ?? (['diagnose', 30, 'Under review', 'Planner'] as const);
    add({
      id: a.id,
      kind: 'Alert',
      title: a.title,
      machine: a.machine,
      mine: BY_ID[a.machine].mine,
      stage: st,
      pct: p,
      next: nx,
      owner: own,
      sev: a.sev === 'crit' ? 'crit' : 'warn',
      action: act('alerts', a.id),
      run: JOB_ALERT_RUN[a.id] ?? null,
    });
  });

  INCIDENTS.forEach((i) => {
    const [p, nx, own] =
      JOB_INCIDENT_STATE[i.id] ?? ([80, 'Lessons into the fleet model', 'Reliability'] as const);
    add({
      id: i.id,
      kind: 'Incident',
      title: i.title,
      machine: i.machine,
      mine: i.mine as MineCode,
      stage: 'closeout',
      pct: p,
      next: nx,
      owner: own,
      sev: 'done',
      // Demo: `renderIncident(i.id); show('incident')`.
      action: act('incident', i.id),
      run: i.id === 'WO-26-11842' ? 'breakdown' : null,
    });
  });

  const seen = new Set(J.map((j) => j.machine));

  ctx.workOrders
    .filter((w) => w.status.startsWith('Draft') || w.status.startsWith('Released'))
    .forEach((w) => {
      if (seen.has(w.machine_id)) return;
      seen.add(w.machine_id);
      const rel = w.status.startsWith('Released');
      add({
        id: w.wo_id,
        kind: rel ? 'Work order · awaiting parts' : 'Agent-drafted work order',
        title: (w.description || '')
          .replace(/^AGENT-DRAFTED[^:]*:\s*/, '')
          .split(' — ')[0]
          .slice(0, 90),
        machine: w.machine_id,
        mine: w.mine,
        stage: rel ? 'parts' : 'promise',
        pct: rel ? 60 : 38,
        next: rel
          ? `Parts on the way — ${w.status.replace('Released - ', '')}`
          : 'Planner approval in Approvals',
        owner: nameOnly(MINES[w.mine].planner),
        sev: w.priority.startsWith('1') ? 'crit' : 'warn',
        action: act('approvals'),
        run: null,
      });
    });

  ctx.techJobs
    .filter((j) => j.status === 'In progress')
    .forEach((j) => {
      // Tests the list, not `seen` — the demo's asymmetry, kept.
      if (J.some((x) => x.id === j.wo_ref)) return;
      seen.add(j.machine);
      add({
        id: j.job_id,
        kind: 'Job in progress',
        title: j.findings || j.job_type,
        machine: j.machine,
        mine: BY_ID[j.machine].mine,
        stage: 'execute',
        pct: 72,
        next: `Technician on the job — ${j.technician}`,
        owner: j.technician,
        sev: 'info',
        action: act('jobcards', j.job_id),
        run: null,
      });
    });

  ctx.cases
    .filter((c) => /Technical query|Parts enquiry/.test(c.type) && /Open/.test(c.status))
    .forEach((c) => {
      seen.add(c.machine_id);
      add({
        id: c.case_no,
        kind: c.type,
        title: c.subject,
        machine: c.machine_id,
        mine: c.mine,
        stage: 'orchestrate',
        pct: /agent/.test(c.status) ? 35 : 20,
        next: /agent/.test(c.status)
          ? `Agent draft with the case owner — ${c.owner}`
          : `Case open with ${c.owner}`,
        owner: c.owner,
        sev: 'info',
        action: act('audit', `cases:${c.case_no}`),
        run: null,
      });
    });

  ctx.cases
    .filter((c) => /Warranty/.test(c.type))
    .forEach((c) => {
      const done = /Declined|Closed/.test(c.status);
      add({
        id: c.case_no,
        kind: c.type,
        title: c.subject,
        machine: c.machine_id,
        mine: c.mine,
        stage: 'commercial',
        pct: done ? 100 : 55,
        next: done
          ? `Closed — ${c.status.split(' — ')[0]}`
          : `Assessment against the BMS log and the thermal-spread precursor — ${c.owner}`,
        owner: c.owner,
        sev: done ? 'done' : 'warn',
        action: act('audit', `cases:${c.case_no}`),
        run: null,
      });
    });

  HEALTH.filter((h) => healthBand(h) === 'poor').forEach((h) => {
    if (seen.has(h.machine_id)) return;
    seen.add(h.machine_id);
    const mine = BY_ID[h.machine_id].mine;
    add({
      id: `${h.machine_id} · ${h.component}`,
      kind: 'Watch condition',
      title: `${h.component} — ${h.note || `condition ${h.health_score}/100`}`,
      machine: h.machine_id,
      mine,
      stage: 'demand',
      pct: 12,
      next: 'Inspect at the next crib; plan the change-out inside the window',
      owner: nameOnly(MINES[mine].maint_super),
      sev: 'warn',
      action: act('machine', h.machine_id),
      run: null,
    });
  });

  add({
    id: 'ONB-MR-PM',
    kind: 'Onboarding',
    title: 'Mount Read — load the Western Tiers contract PM plan beside the OEM plan',
    machine: null,
    mine: 'MR',
    stage: 'installed',
    pct: 35,
    next: 'Plan reconciliation by machine (Lena Sjoberg, next week)',
    owner: 'Lena Sjoberg',
    sev: 'info',
    action: act('population'),
    run: null,
  });
  add({
    id: 'ONB-KG-L1',
    kind: 'Onboarding',
    title: 'Kalbarri joins the pilot at Level 1 — Pitram export back-tested each morning',
    machine: null,
    mine: 'KG',
    stage: 'installed',
    pct: 60,
    next: '250 h sampling on every ML-21 from Monday; overdue list in the S.O.S report',
    owner: 'Alicia Fenton',
    sev: 'info',
    action: act('connectors'),
    run: null,
  });
  add({
    id: 'CAP-HV-LF',
    kind: 'Capacity',
    title:
      'Lucas Ferreira — HV certificate expires in 18 days; the only other HV-current tech is on R&R',
    machine: null,
    mine: 'BR',
    stage: 'capacity',
    pct: 30,
    next: 'Book the HV refresher before the L-07 module fit is repeated on L-12',
    owner: 'Lena Sjoberg',
    sev: 'warn',
    action: act('techs'),
    run: null,
  });
  add({
    id: 'CAP-DTH-NT',
    kind: 'Capacity',
    title:
      'Kieran Walsh is the only DTH-certified technician in the NT — S-04 exchange and S-05 follow-ups land the same week',
    machine: null,
    mine: 'OH',
    stage: 'capacity',
    pct: 20,
    next: 'Remote expert on the S-04 exchange; DTH course for the Darwin technician',
    owner: 'Mei Lin Tan',
    sev: 'info',
    action: act('techs'),
    run: null,
  });
  add({
    id: 'REN-BRKR',
    kind: 'Renewal',
    title:
      'SC-BRKR-2024-01 (Broken Ridge, Fleet Care) ends 31 Dec — renewal proposal drafted with the RMS option',
    machine: null,
    mine: 'BR',
    stage: 'commercial',
    pct: 25,
    next: 'Quarterly review with Helen Kaur — CSS and availability in the pack',
    owner: 'Lena Sjoberg',
    sev: 'info',
    action: act('loops', 'commercial'),
    run: null,
  });
  add({
    id: 'UPG-KALB',
    kind: 'Contract upgrade',
    title: 'Kalbarri call-off → Fleet Care proposal after the second early coupling failure',
    machine: null,
    mine: 'KG',
    stage: 'commercial',
    pct: 15,
    next: 'Cost of the two failures against the contract price — Craig Dunmore',
    owner: 'Lena Sjoberg',
    sev: 'info',
    action: act('loops', 'commercial'),
    run: null,
  });

  return J;
}

/**
 * Demo line 2964. `jobs`.
 *
 * The demo memoised into a module-level `JOBS` cache (`let JOBS=null; const
 * jobs=()=>JOBS||(JOBS=buildJobs())`). **The port keeps no cache**: this is a
 * pure function of its context and allocates every call. A scene that needs the
 * list more than once per render should hoist it into a `useMemo`.
 */
export const jobs = (ctx: WorkOrderCtx & TechJobCtx & CaseCtx): OemJob[] => buildJobs(ctx);

/** Demo line 3203. `siteJobs`. */
export const siteJobs = (mine: MineCode, ctx: WorkOrderCtx & TechJobCtx & CaseCtx): OemJob[] =>
  jobs(ctx).filter((j) => j.mine === mine);

// ===========================================================================
// KPIs and summaries
// ===========================================================================

/**
 * Demo line 2098. What a `todayItems` row offers.
 *
 * The demo wrote each as an inline `onclick`; the four shapes it used are
 * modelled as a discriminated union so nothing carries a handler string.
 */
export type TodayAction =
  /** `show(view, sub)`. */
  | { readonly kind: 'view'; readonly label: string; readonly action: OemAction }
  /** `ask('…')` — a prompt handed to the assistant. */
  | { readonly kind: 'ask'; readonly label: string; readonly prompt: string }
  /** `audit(dataset, filter)` — open the rows behind a figure. */
  | {
      readonly kind: 'audit';
      readonly label: string;
      readonly dataset: string;
      readonly filter: Readonly<Record<string, string>>;
      /**
       * The one demo filter that was a predicate, not an equality:
       * `audit('inv',{qty_on_hand:(v,r)=>v<r.min_level})`.
       */
      readonly predicate?: 'below-min';
    }
  /** `openDoc(ref)`. */
  | { readonly kind: 'doc'; readonly label: string; readonly ref: string };

/** Demo line 2098. One line of the persona's "Today" panel. */
export interface TodayItem {
  /** The band token colouring the bullet: `major | poor | fair | good`. */
  readonly k: ConditionBand;
  /** The line, as written. */
  readonly t: string;
  readonly acts: readonly TodayAction[];
}

const view = (label: string, action: OemAction): TodayAction => ({ kind: 'view', label, action });
const ask = (label: string, prompt: string): TodayAction => ({ kind: 'ask', label, prompt });
const audit = (
  label: string,
  dataset: string,
  filter: Readonly<Record<string, string>>,
  predicate?: 'below-min',
): TodayAction => ({ kind: 'audit', label, dataset, filter, ...(predicate ? { predicate } : {}) });
const doc = (label: string, ref: string): TodayAction => ({ kind: 'doc', label, ref });

/**
 * Demo line 2098. `todayItems` — the persona's morning list.
 *
 * Every sentence is the demo's, including the figures it hard-codes (the
 * A$44/33/28 cost line, the two-failure coupling trend, the 08 Sep pilot date).
 * `pmdue` uses the demo's literal `'2026-09-16'` ceiling, not `daysTo`.
 */
export function todayItems(
  persona: OemPersonaId,
  ctx: WorkOrderCtx & InventoryCtx & CssCtx & TechJobCtx & TechPresenceCtx & TrainingCtx & OrderCtx,
): TodayItem[] {
  const drafts = draftWorkOrders(ctx);
  const low = siteLinesBelowMin(ctx);
  const pmdue = ctx.workOrders.filter(
    (w) => w.wo_type === 'Preventive' && w.status === 'Scheduled' && w.created <= '2026-09-16',
  );
  const bad = FLEET.filter((f) => ATTENTION_BANDS.includes(statusOf(f.machine_id))).sort(
    (a, b) => CONDITION_BAND_ORDER[statusOf(a.machine_id)] - CONDITION_BAND_ORDER[statusOf(b.machine_id)],
  );
  const it = (k: ConditionBand, t: string, ...acts: TodayAction[]): TodayItem => ({ k, t, acts });

  switch (persona) {
    case 'rel':
      return [
        it(
          'major',
          `${bad.filter((f) => statusOf(f.machine_id) === 'major').length} major issue, ${bad.filter((f) => statusOf(f.machine_id) === 'vpoor').length} very poor, ${bad.filter((f) => statusOf(f.machine_id) === 'poor').length} poor — ${bad
            .slice(0, 4)
            .map((f) => f.machine_id)
            .join(', ')}`,
          view('Fleet health', act('fleet')),
        ),
        it(
          'poor',
          'Coupling failure trend at Kalbarri: 2 early failures in 7 months, TB-2026-031 issued',
          view('Incident', act('incident')),
        ),
        it(
          'fair',
          'Weekly reliability & cost review Thursday 14:00 — cost per operating hour KG A$44 · MI A$33 · BR A$28',
          view('Costs', act('costs')),
        ),
        it(
          'good',
          "Ask the assistant for this week's status report or the Broken Ridge summary deck",
          ask('Ask', 'Generate the weekly status report for Load & Haul'),
        ),
      ];
    case 'rsm':
      return [
        it(
          'major',
          `${drafts.length} agent-drafted work orders and 1 restriction awaiting approval`,
          view('Approvals', act('approvals')),
        ),
        it(
          'poor',
          'Warranty case CS-2026-2210 declined (L-02, contamination) — customer conversation with Craig Dunmore',
          audit('Case', 'cases', { case_no: 'CS-2026-2210' }),
        ),
        it(
          'fair',
          'Kalbarri joined the pilot at L1 on 08 Sep — Pitram export connected',
          view('Connectors', act('connectors')),
        ),
        it(
          'good',
          'Broken Ridge equipment summary deck ready for the quarterly review',
          ask('Generate', 'Create the Broken Ridge equipment summary presentation'),
        ),
      ];
    case 'tech':
      return [
        it(
          'major',
          'J-02 trailing cable and slip-ring this morning (two earth-leakage trips overnight) — WO-26-11909; cable is in the KG store',
          view('J-02', act('machine', 'J-02')),
        ),
        it(
          'major',
          'L-02 follow-up oil sample due at +50 h (≈ today) — ISO target 18/16/13',
          view('L-02', act('machine', 'L-02')),
        ),
        it(
          'poor',
          "L-04 rear brake disc pack below limit — approve WO-26-11911, level tramming only; GET tips at 70% can go on while it's down",
          view('L-04', act('machine', 'L-04')),
        ),
        it(
          'fair',
          'PM-250 on J-01 scheduled 11 Sep — procedure SP and LOTO-UG-003 attached',
          doc('Procedure', 'doc:LOTO-UG-003'),
        ),
        it(
          'good',
          'Headset transcript of the L-02 coupling job is in Data for reference',
          doc('Open', 'vtt:AR-KG-20260903-DW.vtt'),
        ),
      ];
    case 'parts':
      return [
        it(
          'major',
          'MER-RD-FE-KIT: BR store 0 · Newcastle DC 0 — SO-90418 air freight from Perth awaiting approval',
          view('Approve', act('approvals')),
        ),
        it(
          'poor',
          `${low.length} site-store lines below minimum across three mines`,
          audit('Stock', 'inv', {}, 'below-min'),
        ),
        it(
          'fair',
          'Newcastle DC minimum for front-end kits under review (action from Thursday)',
          audit('Catalogue', 'parts', { criticality: 'Critical' }),
        ),
        it(
          'good',
          '3 × MER-BRK-PAD-63 in MI store — one reserved for T-09, no order needed',
          view('T-09', act('machine', 'T-09')),
        ),
      ];
    case 'planner':
      return [
        it(
          'major',
          'T-09 rear brakes: approve WO-26-11912 for 10:00 crib and the empty-haul restriction',
          view('Approve', act('approvals')),
        ),
        it(
          'poor',
          'L-13 derates on 1,450 level — air filter added to PM-250 due 11 Sep',
          view('L-13', act('machine', 'L-13')),
        ),
        it(
          'fair',
          `${pmdue.filter((w) => w.mine === 'MI').length} PMs due at Mount Isolde in the next 7 days`,
          audit('PM list', 'wos', { mine: 'MI', wo_type: 'Preventive', status: 'Scheduled' }),
        ),
        it(
          'good',
          "Seismic exclusion 07 Sep coded to ground conditions — not on Redgum's numbers",
          doc('Protocol', 'doc:MI-SEIS-002'),
        ),
      ];
    case 'aftermarket': {
      const css = ctx.css.map((r) => r.Overall);
      const cssAvg = (css.reduce((a, b) => a + b, 0) / css.length).toFixed(1);
      const openJobs = ctx.techJobs.filter((j) => j.status !== 'Completed').length;
      const lowStore = low.length;
      const lmsExp = ctx.training.filter(
        (r) => r.status !== 'Current' && r.org === 'Meridian Equipment',
      ).length;
      return [
        it(
          'major',
          `S.O.S hotspots this morning: ${bad.filter((f) => ['major', 'vpoor'].includes(statusOf(f.machine_id))).length} machines very poor or worse across ${new Set(bad.map((f) => f.mine)).size} sites — two incidents this week (IR-2026-014, IR-2026-015)`,
          view('S.O.S report', act('sos')),
        ),
        it(
          'poor',
          `Parts pipeline: ${ctx.orders.filter((o) => /agent/i.test(o.status) && /Draft/.test(o.status)).length} agent requisitions waiting, ${lowStore} store lines below minimum, one exchange hammer reserved at Ochre Hill`,
          view('Pipeline', act('partsflow')),
        ),
        it(
          'fair',
          `Technicians: ${ctx.techs.filter((t) => t.status === 'On site').length} of ${ctx.techs.length} on site, ${openJobs} open technician-app jobs, ${lmsExp} certificates expiring or expired`,
          view('Technicians', act('techs')),
        ),
        it(
          'good',
          `Customer satisfaction ${cssAvg}/5 over ${css.length} surveyed jobs — Ochre Hill rated the string recovery 5/5`,
          view('Customers', act('customers')),
        ),
      ];
    }
  }
}

/** Demo line 2967. One of the control tower's eight KPI tiles. */
export interface ControlTowerKpi {
  /** Tile eyebrow, e.g. `'Cost summary'`. */
  readonly label: string;
  /** The headline figure, already formatted where the demo formatted it. */
  readonly value: string | number;
  /** The unit line under the figure. */
  readonly unit: string;
  /** The detail line, with its embedded figures interpolated. */
  readonly detail: string;
  readonly action: OemAction;
  /** Tone token: `'good' | 'warn' | 'bad'`. */
  readonly tone: string;
}

/**
 * Demo line 2967. `ctKPIs` — the poster's eight control-tower figures.
 *
 * The availability figure is {@link avgAvail}'s — sum over the machines that
 * have one, divided by all 100. The CSS average is the mean of the `Overall`
 * column to one decimal, as a string, and the tile's tone compares that string
 * against `4` — a string/number comparison that JavaScript coerces and that is
 * kept as written.
 */
export function ctKPIs(
  ctx: WorkOrderCtx &
    TechJobCtx &
    ShiftCtx &
    CssCtx &
    ContractCtx &
    MaximoCtx &
    InventoryCtx &
    OrderCtx &
    PartsCatalogueCtx &
    TechPresenceCtx &
    TimesheetCtx &
    TrainingCtx &
    BulletinCtx &
    CaseCtx,
): ControlTowerKpi[] {
  const rv = revenue(ctx);
  const bands = (k: ConditionBand): number =>
    FLEET.filter((f) => statusOf(f.machine_id) === k).length;
  const av = avgAvail(ctx);
  const css = ctx.css.map((r) => r.Overall);
  const cssAvg = (css.reduce((a, b) => a + b, 0) / css.length).toFixed(1);
  const rn = renewals(ctx)[0];
  const lowSite = siteLinesBelowMin(ctx);

  return [
    {
      label: 'Cost summary',
      value: fmtk(rv.parts12),
      unit: 'parts spend · 12 months',
      detail: `${fmtk(rv.labour10w)} technician labour in 10 weeks · ${Math.round(rv.reactiveShare * 100)}% of parts spend is reactive · ${fmtk(rv.freightBd)} of breakdown freight`,
      action: act('costs'),
      tone: 'warn',
    },
    {
      label: 'Installed base & asset health',
      value: FLEET.length,
      unit: `machines · ${av}% available`,
      detail: `${bands('major')} major · ${bands('vpoor')} very poor · ${bands('poor')} poor · ${bands('fair')} fair · ${bands('good')} good`,
      action: act('module', 'installed'),
      tone: FLEET.filter((f) => ['major', 'vpoor'].includes(statusOf(f.machine_id))).length
        ? 'bad'
        : 'warn',
    },
    {
      label: 'Parts inventory & supply',
      value: lowSite.length,
      unit: 'site lines below minimum',
      detail: `${ctx.orders.filter((o) => /agent/i.test(o.status)).length} requisitions waiting · ${ctx.orders.filter((o) => /air/i.test(o.status)).length} on air · ${predictedUsage(ctx).filter((r) => r.gap > 0).length} predicted gaps`,
      action: act('module', 'parts'),
      tone: lowSite.length ? 'warn' : 'good',
    },
    {
      label: 'Service capacity & utilisation',
      value: `${ctx.techs.filter((t) => t.status === 'On site').length}/${ctx.techs.length}`,
      unit: 'technicians on site',
      detail: `${meanUtil(ctx)}% utilisation · ${ctx.techJobs.filter((j) => j.status !== 'Completed').length} open jobs · ${ctx.training.filter((r) => r.status !== 'Current' && r.org === 'Meridian Equipment').length} certificates to renew`,
      action: act('module', 'capacity'),
      tone: meanUtil(ctx) >= 70 ? 'good' : 'warn',
    },
    {
      label: 'Contracts & commercial',
      value: ctx.contracts.length,
      unit: 'site contracts · 100% covered',
      detail: `${rn.customer} renews in ${rn.days} days · ${upgradeCandidates(ctx).length} upgrade candidates · ${warrantyActive(ctx).length} under warranty`,
      action: act('module', 'commercial'),
      tone: 'good',
    },
    {
      label: 'Warranty, quality & campaigns',
      value: ctx.bulletins.length,
      unit: 'bulletins in force',
      detail: `${ctx.cases.filter((c) => /Warranty/.test(c.type)).length} warranty cases · 1 campaign running (ML-21 hoses, 1 of 4) · 3 bulletins this fortnight`,
      action: act('loops', 'warranty'),
      tone: 'warn',
    },
    {
      label: 'Component rebuilds & cores',
      value: EXCHANGE.length,
      unit: 'exchange units in play',
      detail: `${pastInterval().length} components past interval · 2 cores in the rebuild queue`,
      action: act('loops', 'component'),
      tone: pastInterval().length ? 'warn' : 'good',
    },
    {
      label: 'Customer satisfaction',
      value: cssAvg,
      unit: '/ 5 this month',
      detail: `${css.length} responses · ${css.filter((v) => v < 3.5).length} below 3.5 · reporting is the lowest score`,
      // The demo compares the formatted string against the number 4.
      action: act('customers'),
      tone: Number(cssAvg) >= 4 ? 'good' : 'warn',
    },
  ];
}

// ---------------------------------------------------------------------------
// Asset360 — issue summary
// ---------------------------------------------------------------------------

/** Demo line 2980. One of the four `issueSummary` tiles. */
export interface IssueTile {
  /** Eyebrow, e.g. `'Open alerts'`. */
  readonly eyebrow: string;
  readonly value: number;
  /** The line under the figure. */
  readonly detail: string;
  /** Tone token: `'bad' | 'warn' | 'good'`. */
  readonly tone: string;
  readonly action: OemAction;
}

/** Demo line 2980. One of the four alert rows under the tiles. */
export interface IssueRow {
  readonly alert: Alert;
  readonly machine: Machine;
  /** Site display name. */
  readonly site: string;
  readonly crit: boolean;
  readonly action: OemAction;
}

/** Demo line 2980. `issueSummary`, data only. */
export interface IssueSummary {
  readonly tiles: readonly IssueTile[];
  readonly rows: readonly IssueRow[];
  /** The "all issues" link in the header. */
  readonly allIssues: OemAction;
}

/**
 * Demo line 2980. `issueSummary`.
 *
 * The row sort is the demo's `(a.sev==='crit'?0:1)-(b.sev==='crit'?0:1)` —
 * critical first, everything else in seed order, stable.
 */
export function issueSummary(ctx: WorkOrderCtx): IssueSummary {
  const attn = FLEET.filter((f) => ATTENTION_BANDS.includes(statusOf(f.machine_id)));
  const crit = ALERTS.filter((a) => a.sev === 'crit');
  const drafts = draftWorkOrders(ctx);
  return {
    tiles: [
      {
        eyebrow: 'Open alerts',
        value: ALERTS.length,
        detail: `${crit.length} critical · ${ALERTS.filter((a) => a.mode === 'LIVE').length} live`,
        tone: crit.length ? 'bad' : 'warn',
        action: act('alerts'),
      },
      {
        eyebrow: 'Machines to watch',
        value: attn.length,
        detail: `${FLEET.filter((f) => ['major', 'vpoor'].includes(statusOf(f.machine_id))).length} very poor or worse`,
        tone: 'bad',
        action: act('fleet'),
      },
      {
        eyebrow: 'Incidents',
        value: INCIDENTS.length,
        detail: 'closed this fortnight · replayed',
        tone: INCIDENTS.length ? 'warn' : 'good',
        action: act('incident'),
      },
      {
        eyebrow: 'Waiting on a person',
        value: drafts.length,
        detail: 'agent-drafted work orders',
        tone: drafts.length ? 'warn' : 'good',
        action: act('approvals'),
      },
    ],
    rows: ALERTS.slice()
      .sort((a, b) => (a.sev === 'crit' ? 0 : 1) - (b.sev === 'crit' ? 0 : 1))
      .slice(0, 4)
      .map((a) => {
        const f = BY_ID[a.machine];
        return {
          alert: a,
          machine: f,
          site: MINES[f.mine].name,
          crit: a.sev === 'crit',
          action: act('alerts', a.id),
        };
      }),
    allIssues: act('section', 'issues'),
  };
}

/** Demo line 2990. `renderAsset360`'s job grouping — jobs by loop stage. */
export const jobsByStage = (
  stage: JobStage,
  ctx: WorkOrderCtx & TechJobCtx & CaseCtx,
): OemJob[] => jobs(ctx).filter((j) => j.stage === stage);

// ---------------------------------------------------------------------------
// Issues page
// ---------------------------------------------------------------------------

/** Demo line 3115. `ISSUE_TABS`. */
export const ISSUE_TABS = [
  ['now', 'Live'],
  ['alerts', 'Alerts'],
  ['incidents', 'Incidents'],
  ['watch', 'Watch list'],
] as const satisfies readonly (readonly [string, string])[];

/** Demo line 3115. A tab key on the Issues page. */
export type IssueTab = (typeof ISSUE_TABS)[number][0];

/** Demo line 3165. `renderIssues`'s header and tab counts. */
export interface IssueCounts {
  readonly critical: number;
  readonly watch: number;
  readonly incidents: number;
  /** The alert count on the Alerts tab. */
  readonly alerts: number;
  /** Components in an attention band — the Watch-list tab count. */
  readonly watchList: number;
}

/** Demo line 3165. `renderIssues`, counts only. */
export const issueCounts = (): IssueCounts => ({
  critical: ALERTS.filter((a) => a.sev === 'crit').length,
  watch: ALERTS.filter((a) => a.sev !== 'crit').length,
  incidents: INCIDENTS.length,
  alerts: ALERTS.length,
  watchList: HEALTH.filter((h) => ATTENTION_BANDS.includes(healthBand(h))).length,
});

/** Demo line 3121. `issueHero` — the four numbers across the top of Issues. */
export interface IssueHero {
  readonly criticalAlerts: number;
  /** Alerts raised this morning. */
  readonly newToday: number;
  readonly liveAlerts: number;
  /** Total cost of waiting across the alerts that have a `COI` row. */
  readonly exposure: number;
  /** {@link IssueHero.exposure}, formatted the way the tile prints it. */
  readonly exposureLabel: string;
  /** Jobs whose next step names a person's approval. */
  readonly waiting: number;
  readonly draftedWorkOrders: number;
  readonly draftRequisitions: number;
  /** Restrictions and holds in force. */
  readonly restrictions: number;
  /** Machines in `major` or `vpoor`. */
  readonly veryPoorOrWorse: number;
  /** Components sitting in `poor` — the watch list. */
  readonly watchComponents: number;
}

/**
 * Demo line 3121. `issueHero`.
 *
 * `restrictions` is the demo's hard-coded `const restr=5` — a literal, not a
 * count of anything in the data. Kept, deliberately, so the tile reads as it
 * does on the original screen.
 *
 * `waiting` matches `/Approve|approval|Planner/i` against each job's `next`
 * line, which is exactly how the demo counts it.
 */
export function issueHero(ctx: WorkOrderCtx & TechJobCtx & CaseCtx & OrderCtx): IssueHero {
  const exposure = totalCostOfInaction();
  const restr = 5; // demo's literal — see the doc comment above
  return {
    criticalAlerts: ALERTS.filter((a) => a.sev === 'crit').length,
    newToday: ALERTS.filter(newSince).length,
    liveAlerts: ALERTS.filter((a) => a.mode === 'LIVE').length,
    exposure,
    exposureLabel: fmtk(exposure),
    waiting: jobs(ctx).filter((j) => /Approve|approval|Planner/i.test(j.next)).length,
    draftedWorkOrders: draftWorkOrders(ctx).length,
    draftRequisitions: ctx.orders.filter((o) => /Draft/.test(o.status)).length,
    restrictions: restr,
    veryPoorOrWorse: FLEET.filter((f) => ['major', 'vpoor'].includes(statusOf(f.machine_id))).length,
    watchComponents: HEALTH.filter((h) => healthBand(h) === 'poor').length,
  };
}

/** Demo line 3130. `alertCard` — everything the card derives, no markup. */
export interface AlertCardData {
  readonly alert: Alert;
  readonly machine: Machine;
  /** Site display name. */
  readonly site: string;
  /** Short site name, as the caption prints it. */
  readonly siteShort: string;
  readonly coi: Coi | null;
  readonly window: WindowState;
  /** Condition score of the alert's component, `0` when the register has none. */
  readonly score: number;
  readonly band: ConditionBandSpec;
  readonly newToday: boolean;
  /** Chain steps still waiting on a person. */
  readonly waiting: number;
  /** Who the alert is waiting on — its job's owner, else the site planner. */
  readonly owner: string;
  /** The first two sentences of `why`, re-punctuated as the demo does. */
  readonly whyShort: string;
  readonly openChain: OemAction;
  readonly openMachine: OemAction;
  readonly openApprovals: OemAction;
}

/**
 * Demo line 3130. `alertCard`, data only.
 *
 * `whyShort` reproduces the demo's `why.split('. ').slice(0,2).join('. ')`
 * followed by stripping a trailing full stop and adding one back — so a
 * two-sentence excerpt always ends in exactly one `.`.
 */
export function alertCardData(
  alert: Alert,
  ctx: WorkOrderCtx & TechJobCtx & CaseCtx,
): AlertCardData {
  const f = BY_ID[alert.machine];
  const h = healthOf(alert.machine).find((x) => x.component === alert.comp);
  const sc = h ? h.health_score : 0;
  const job = jobs(ctx).find((j) => j.id === alert.id);
  return {
    alert,
    machine: f,
    site: MINES[f.mine].name,
    siteShort: MSHORT[f.mine],
    coi: coi(alert),
    window: windowPct(alert),
    score: sc,
    band: bandOf(sc),
    newToday: newSince(alert),
    waiting: alert.chain.filter((x) => x.status === 'approve').length,
    owner: job ? job.owner : nameOnly(MINES[f.mine].planner),
    whyShort: `${alert.why.split('. ').slice(0, 2).join('. ').replace(/\.$/, '')}.`,
    openChain: act('alerts', alert.id),
    openMachine: act('machine', alert.machine),
    openApprovals: act('approvals'),
  };
}

/** Demo line 2279. `alertPage` — the condition figure beside the agent chain. */
export interface AlertPageData {
  readonly alert: Alert;
  readonly machine: Machine;
  readonly site: string;
  /** `Math.round(conf * 100)` — the model-confidence percentage. */
  readonly confidencePct: number;
  /** Condition score of the alert's component, `0` when absent. */
  readonly score: number;
  readonly band: ConditionBandSpec;
  readonly openMachine: OemAction;
}

/** Demo line 2279. `alertPage`, data only. */
export function alertPageData(alert: Alert): AlertPageData {
  const f = BY_ID[alert.machine];
  const sc = healthOf(alert.machine).find((x) => x.component === alert.comp)?.health_score ?? 0;
  return {
    alert,
    machine: f,
    site: MINES[f.mine].name,
    confidencePct: Math.round(alert.conf * 100),
    score: sc,
    band: bandOf(sc),
    openMachine: act('machine', alert.machine),
  };
}

/** Demo line 2287. `renderAlerts` — the picker's summary line for one alert. */
export interface AlertPickerEntry {
  readonly alert: Alert;
  readonly machine: Machine;
  readonly site: string;
  /** `'CRIT'` or `'WATCH'`. */
  readonly tag: string;
  readonly action: OemAction;
}

/** Demo line 2287. `renderAlerts`'s left-hand picker, data only. */
export const alertPickerEntries = (): AlertPickerEntry[] =>
  ALERTS.map((a) => {
    const f = BY_ID[a.machine];
    return {
      alert: a,
      machine: f,
      site: MINES[f.mine].name,
      tag: a.sev === 'crit' ? 'CRIT' : 'WATCH',
      action: act('alerts', a.id),
    };
  });

// ---------------------------------------------------------------------------
// The 24-hour ticker
// ---------------------------------------------------------------------------

/** Demo line 3141. One row of the event ticker. */
export interface TickerEvent {
  /** Display timestamp, e.g. `'09 Sep 04:41'`. */
  readonly t: string;
  /** Kind label, e.g. `'Alert'`, `'RMS Act'`, `'Job done'`. */
  readonly k: string;
  /** Tone token: `'crit' | 'warn' | 'good' | 'info'`. */
  readonly sev: string;
  readonly txt: string;
  readonly action: OemAction;
  /**
   * The alert card the row scrolls to once the Issues page has switched tabs —
   * the demo's `document.getElementById('ic-<id>')`.
   */
  readonly focus?: string;
}

/**
 * Demo line 3141. `ticker`.
 *
 * The sort key is the demo's: `DD` + `HH` + `mm` pulled off the display string
 * with a regex, missing time parts defaulting to `'00'`, and `'0'` when the
 * string does not start `DD Sep`. Compared with `localeCompare`, descending,
 * then the top 14 are kept.
 *
 * The `'2026-09-08'` cutoffs and the closing return-to-service row are the
 * demo's literals.
 */
export function ticker(
  ctx: RmsAlertCtx & TechJobCtx & RemoteSessionCtx & OrderCtx,
): TickerEvent[] {
  const ev: TickerEvent[] = [];

  ALERTS.forEach((a) =>
    ev.push({
      t: a.raised,
      k: 'Alert',
      sev: a.sev === 'crit' ? 'crit' : 'warn',
      txt: `${a.machine} — ${a.title}`,
      action: act('issues', 'alerts'),
      focus: `ic-${a.id}`,
    }),
  );

  ctx.rmsAlerts
    .filter((r) => r.issued >= '2026-09-08')
    .forEach((r) =>
      ev.push({
        t: `${r.issued.slice(8, 10)} Sep ${r.issued.slice(11)}`,
        k: `RMS ${r.severity}`,
        sev: r.severity === 'Act' ? 'crit' : 'warn',
        txt: `${r.machine} — ${r.signature.slice(0, 90)}`,
        action: act('machine', r.machine),
      }),
    );

  ctx.techJobs
    .filter((j): j is TechnicianJob & { off_site: string } =>
      Boolean(j.off_site && j.off_site >= '2026-09-08'),
    )
    .forEach((j) =>
      ev.push({
        t: `${j.off_site.slice(8, 10)} Sep ${j.off_site.slice(11, 16)}`,
        k: 'Job done',
        sev: 'good',
        txt: `${j.job_id} on ${j.machine} completed by ${j.technician}`,
        action: act('jobcards', j.job_id),
      }),
    );

  ctx.remoteSessions
    .filter((s) => s.started >= '2026-09-08')
    .forEach((s) =>
      ev.push({
        t: `${s.started.slice(8, 10)} Sep ${s.started.slice(11, 16)}`,
        k: 'Remote',
        sev: 'info',
        txt: `${s.session_id} — ${s.field_worker.split(' ')[0]} → ${s.expert.split(' ')[0]} on ${s.machine}`,
        action: act('remote', s.session_id),
      }),
    );

  ctx.orders
    .filter((o) => o.order_date >= '2026-09-08')
    .forEach((o) =>
      ev.push({
        t: `${o.order_date.slice(8, 10)} Sep`,
        k: 'Parts',
        sev: 'info',
        txt: `${o.order_no} · ${o.part_no} for ${o.machine_id} — ${o.status.split(' · ')[0]}`,
        action: act('partsflow'),
      }),
    );

  ev.push({
    t: '09 Sep 05:40',
    k: 'Return to service',
    sev: 'good',
    txt: 'L-18 back on the northern decline after the engine bay fire — turbo, ten hoses, suppression recharge and a heat-soak test',
    action: act('incident', 'IR-2026-015'),
  });

  const key = (s: string): string => {
    const m = /^(\d\d) Sep(?: (\d\d):(\d\d))?/.exec(s);
    return m ? m[1] + (m[2] ?? '00') + (m[3] ?? '00') : '0';
  };
  return ev.sort((a, b) => key(b.t).localeCompare(key(a.t))).slice(0, 14);
}

// ---------------------------------------------------------------------------
// Site heat, incident strip, watch table
// ---------------------------------------------------------------------------

/** Demo line 3151. One site row in the heat strip. */
export interface SiteHeatRow {
  readonly mine: MineCode;
  readonly name: string;
  readonly state: string;
  readonly machines: number;
  /**
   * Band counts in legend order — good first, major last (the demo's
   * `BANDS.slice().reverse()`). Zero-count bands are dropped by the renderer.
   */
  readonly bands: readonly { readonly band: ConditionBandSpec; readonly n: number }[];
  readonly alerts: number;
  readonly incidents: number;
  readonly action: OemAction;
}

/** Demo line 3151. `siteHeat`, data only. */
export const siteHeat = (): SiteHeatRow[] =>
  MINE_ORDER.map((m) => {
    const fs = FLEET.filter((f) => f.mine === m);
    return {
      mine: m,
      name: MINES[m].name,
      state: MINES[m].state,
      machines: fs.length,
      bands: bandsBestFirst().map((b) => ({
        band: b,
        n: fs.filter((f) => statusOf(f.machine_id) === b.key).length,
      })),
      alerts: ALERTS.filter((a) => BY_ID[a.machine].mine === m).length,
      incidents: INCIDENTS.filter((i) => i.mine === m).length,
      action: act('sites', m),
    };
  });

/** Reads an {@link IncidentKpiValue}'s literal text; `''` for a cost field. */
const kpiText = (v: IncidentKpiValue): string => (v.kind === 'text' ? v.text : '');

/** Demo line 3154. One incident card in the strip. */
export interface IncidentStripCard {
  readonly incident: Incident;
  /** The work order the costs are read from, derived the demo's way. */
  readonly workOrderId: string;
  readonly machine: Machine;
  /** `total_direct_cost_aud + downtime_cost_aud`, `0` when the order is absent. */
  readonly actual: number;
  /** `INC_PLANNED[id]`, `0` when absent. */
  readonly planned: number;
  /** `actual` as a percentage of the strip's largest actual, rounded. */
  readonly actualPct: number;
  /** `planned` against the same denominator. */
  readonly plannedPct: number;
  /** Days of warning already in the data — KPI 3's headline. */
  readonly warningDays: string;
  /** What it was attributed to — KPI 4's headline. */
  readonly attribution: string;
  /** The standing rule the close-out produced, `'—'` when none matches. */
  readonly rule: string;
  readonly action: OemAction;
  readonly openMachine: OemAction;
}

/**
 * Demo line 3154. `incidentStrip`, data only.
 *
 * The work-order id is derived as the demo derives it — `i.id` when it starts
 * `WO`, otherwise the second half of `i.ref` split on `' · '` — rather than
 * read off the seed's `workOrder` field, so the two cannot silently diverge.
 *
 * Both bars are scaled against the largest **actual** in the strip, so the
 * planned bar is always the short one.
 */
export function incidentStrip(ctx: WorkOrderCtx): IncidentStripCard[] {
  const woIdOf = (i: Incident): string =>
    i.id.startsWith('WO') ? i.id : i.ref.split(' · ')[1];
  const allIn = (wid: string): number => {
    const w = ctx.workOrders.find((x) => x.wo_id === wid);
    return w ? w.total_direct_cost_aud + w.downtime_cost_aud : 0;
  };
  const mx = Math.max(...INCIDENTS.map((i) => allIn(woIdOf(i))));

  return INCIDENTS.map((i) => {
    const wid = woIdOf(i);
    const actual = allIn(wid);
    const planned = INC_PLANNED[i.id] ?? 0;
    return {
      incident: i,
      workOrderId: wid,
      machine: BY_ID[i.machine],
      actual,
      planned,
      actualPct: Math.round((actual / mx) * 100),
      plannedPct: Math.round((planned / mx) * 100),
      warningDays: kpiText(i.kpis[2].value),
      attribution: kpiText(i.kpis[3].value),
      rule: (
        FLEET_RULES.find((r) => r[1] === i.id) ??
        FLEET_RULES.find((r) => r[1] === wid) ?? ['—']
      )[0],
      action: act('incident', i.id),
      openMachine: act('machine', i.machine),
    };
  });
}

/** Demo line 3162. The demo shows the first 40 rows and notes the remainder. */
export const WATCH_TABLE_LIMIT = 40;

/** Demo line 3162. One row of the watch list. */
export interface WatchRow {
  readonly machine_id: string;
  readonly model: ModelCode;
  /** Short site name, as the column prints it. */
  readonly site: string;
  readonly component: string;
  readonly health_score: number;
  readonly band: ConditionBand;
  readonly hours_since_change: number;
  readonly interval: number;
  /** `interval - hours_since_change`; negative reads "overdue". */
  readonly remaining: number;
  /** The register note, or `null`. The demo prints `'—'` when absent. */
  readonly note: string | null;
  readonly action: OemAction;
}

/**
 * Demo line 3162. `watchTable`, data only — every attention-band component
 * that has **not** already become an alert on that machine and component,
 * worst score first.
 *
 * Returns the whole list; the renderer slices {@link WATCH_TABLE_LIMIT}.
 */
export const watchTable = (): WatchRow[] =>
  HEALTH.filter((h) => {
    const b = healthBand(h);
    return b === 'poor' || b === 'vpoor' || b === 'major';
  })
    .filter((h) => !ALERTS.some((a) => a.machine === h.machine_id && a.comp === h.component))
    .sort((a, b) => a.health_score - b.health_score)
    .map((h) => {
      const f = BY_ID[h.machine_id];
      const interval = intervalOf(h.component);
      return {
        machine_id: h.machine_id,
        model: f.model,
        site: MSHORT[f.mine],
        component: h.component,
        health_score: h.health_score,
        band: healthBand(h),
        hours_since_change: h.hours_since_change,
        interval,
        remaining: interval - h.hours_since_change,
        note: h.note,
        action: act('machine', h.machine_id),
      };
    });

/** Demo line 2322. `renderIncident`'s picker, data only. */
export interface IncidentPickerEntry {
  readonly incident: Incident;
  readonly machine: Machine;
  readonly site: string;
  /** `'IR'` or `'WO'`. */
  readonly tag: string;
  readonly action: OemAction;
}

/** Demo line 2322. `renderIncident`'s left-hand picker. */
export const incidentPickerEntries = (): IncidentPickerEntry[] =>
  INCIDENTS.map((i) => ({
    incident: i,
    machine: BY_ID[i.machine],
    site: MINES[i.mine as MineCode].name,
    tag: i.id.startsWith('IR') ? 'IR' : 'WO',
    action: act('incident', i.id),
  }));

/**
 * Demo line 2322. `renderIncident`'s KPI figures: the work order every cost
 * on an incident is read from, and its all-in total.
 */
export interface IncidentCosts {
  readonly workOrder: WorkOrder | undefined;
  /** `total_direct_cost_aud + downtime_cost_aud`, or `null` with no order. */
  readonly allIn: number | null;
}

/** Demo line 2322. `renderIncident`, cost derivation only. */
export function incidentCosts(incident: Incident, ctx: WorkOrderCtx): IncidentCosts {
  const w = ctx.workOrders.find((x) => x.wo_id === incident.workOrder);
  return { workOrder: w, allIn: w ? w.total_direct_cost_aud + w.downtime_cost_aud : null };
}

// ===========================================================================
// Sites
// ===========================================================================

/** Demo line 3200. `siteCSS` — survey responses for one site. */
export const siteCSS = (mine: MineCode, ctx: CssCtx): CustomerSatisfactionResponse[] =>
  ctx.css.filter((r) => r.Site === MINES[mine].name);

/**
 * Demo line 3204. `siteDocs` — every lake document about a site.
 *
 * Three clauses, any of which matches: the file name contains the site name
 * hyphenated, the document names one of the site's machines, or its text
 * mentions the site. The third is inert against `lake/index.json`, which
 * carries no payload — see {@link LakeIndexEntry}.
 */
export function siteDocs(mine: MineCode, ctx: LakeCtx): LakeIndexEntry[] {
  const nm = MINES[mine].name.replace(/ /g, '-');
  const ids = new Set(FLEET.filter((f) => f.mine === mine).map((f) => f.machine_id));
  return ctx.lakeEntries.filter(
    (e) =>
      e.file.includes(nm) ||
      (e.machines ?? []).some((x) => ids.has(x)) ||
      (e.text ?? '').includes(MINES[mine].name),
  );
}

/** Demo line 3206. `siteHead` — the site page's header figures. */
export interface SiteHead {
  readonly mine: MineCode;
  readonly machines: readonly Machine[];
  /** The site's contract, matched on customer then on machine count. */
  readonly contract: MaximoContract | undefined;
  /** Machines in `major` or `vpoor`. */
  readonly veryPoorOrWorse: number;
  readonly availability: number | null;
  readonly availabilityBand: ConditionBandSpec | null;
  readonly openJobs: number;
  readonly alerts: number;
  readonly drafts: number;
  /** Mean CSS `Overall` to one decimal, or `null` with no responses. */
  readonly css: number | null;
  readonly cssResponses: number;
  readonly battery: number;
  readonly autonomous: number;
  readonly remote: number;
  /** Serving distribution centre. */
  readonly dc: string;
  readonly freightDays: number;
  /** The header spec list, in the demo's order. */
  readonly spec: readonly (readonly [label: string, value: string])[];
}

/**
 * Demo line 3206. `siteHead`, data only.
 *
 * The contract lookup is the demo's two-step fallback: match the contract whose
 * `customer` is the site name, else the one whose `machines` count equals the
 * site's fleet size. The second step is what covers the contractor-operated
 * sites whose contract is booked to the contractor, not the mine.
 */
export function siteHead(
  mine: MineCode,
  ctx: ShiftCtx & CssCtx & ContractCtx & WorkOrderCtx & TechJobCtx & CaseCtx,
): SiteHead {
  const M = MINES[mine];
  const fs = FLEET.filter((f) => f.mine === mine);
  const contract =
    ctx.contracts.find((x) => x.customer === M.name) ??
    ctx.contracts.find((x) => x.machines === fs.length);
  const av = siteAvail(mine, ctx);
  const css = siteCSS(mine, ctx);
  return {
    mine,
    machines: fs,
    contract,
    veryPoorOrWorse: fs.filter((f) => ['major', 'vpoor'].includes(statusOf(f.machine_id))).length,
    availability: av,
    availabilityBand: av != null ? bandOf(av) : null,
    openJobs: siteJobs(mine, ctx).length,
    alerts: ALERTS.filter((a) => BY_ID[a.machine].mine === mine).length,
    drafts: ctx.workOrders.filter((w) => w.mine === mine && w.status.startsWith('Draft')).length,
    css: css.length ? +(css.reduce((a, r) => a + r.Overall, 0) / css.length).toFixed(1) : null,
    cssResponses: css.length,
    battery: fs.filter((f) => f.battery).length,
    autonomous: fs.filter((f) => f.autonomous).length,
    remote: fs.filter((f) => f.remote).length,
    dc: M.dc,
    freightDays: M.freight_days,
    spec: [
      ['Mine type', miningLabel(M)],
      ['Mining method', M.method],
      ['Commodities', M.commodity],
      ['Depth', `${groupNum(M.depth_m)} m`],
      ['Ownership', M.ownership],
      ['Region', `${M.region} (${M.state})`],
    ],
  };
}

/** Demo line 3221. `siteKPIs` — the four tiles on the site overview. */
export interface SiteKpis {
  readonly machines: number;
  readonly battery: number;
  readonly autonomous: number;
  readonly remote: number;
  readonly availability: number | null;
  readonly availabilityBand: ConditionBandSpec | null;
  readonly veryPoorOrWorse: number;
  readonly costPerOpHour: number | null;
  readonly openJobs: number;
  readonly alerts: number;
  readonly drafts: number;
  /** Site-store lines below their minimum. */
  readonly lowLines: number;
  readonly css: number | null;
  readonly cssResponses: number;
  /** Superintendent, parenthetical stripped. */
  readonly maintSuper: string;
  /** Planner, parenthetical stripped. */
  readonly planner: string;
}

/** Demo line 3221. `siteKPIs`, data only. */
export function siteKPIs(
  mine: MineCode,
  ctx: ShiftCtx & CssCtx & InventoryCtx & WorkOrderCtx & TechJobCtx & CaseCtx,
): SiteKpis {
  const M = MINES[mine];
  const fs = FLEET.filter((f) => f.mine === mine);
  const av = siteAvail(mine, ctx);
  const css = siteCSS(mine, ctx);
  return {
    machines: fs.length,
    battery: fs.filter((f) => f.battery).length,
    autonomous: fs.filter((f) => f.autonomous).length,
    remote: fs.filter((f) => f.remote).length,
    availability: av,
    availabilityBand: av != null ? bandOf(av) : null,
    veryPoorOrWorse: fs.filter((f) => ['major', 'vpoor'].includes(statusOf(f.machine_id))).length,
    costPerOpHour: siteCPH(mine),
    openJobs: siteJobs(mine, ctx).length,
    alerts: ALERTS.filter((a) => BY_ID[a.machine].mine === mine).length,
    drafts: ctx.workOrders.filter((w) => w.mine === mine && w.status.startsWith('Draft')).length,
    lowLines: siteStore(mine, ctx).filter((i) => i.qty_on_hand < i.min_level).length,
    css: css.length ? +(css.reduce((a, r) => a + r.Overall, 0) / css.length).toFixed(1) : null,
    cssResponses: css.length,
    maintSuper: nameOnly(M.maint_super),
    planner: nameOnly(M.planner),
  };
}

/** Demo line 3227. One group of the site fleet strip. */
export interface SiteFleetGroup {
  /** Named working, or `null` where the site has no subdivisions. */
  readonly working: string | null;
  readonly machines: readonly Machine[];
}

/**
 * Demo line 3227. `siteFleetStrip`, data only — machines grouped by working,
 * or one unnamed group at the sites that have none. Empty workings are dropped.
 */
export function siteFleetStrip(mine: MineCode): SiteFleetGroup[] {
  const fs = FLEET.filter((f) => f.mine === mine);
  const wk = WORKINGS[mine];
  if (!wk) return [{ working: null, machines: fs }];
  const grp = wk
    .map((w): SiteFleetGroup => ({ working: w, machines: fs.filter((f) => f.working === w) }))
    .filter((x) => x.machines.length);
  return grp.length ? grp : [{ working: null, machines: fs }];
}

/** Demo line 3195. `SITE_TABS`. */
export const SITE_TABS = [
  ['overview', 'Overview'],
  ['fleet', 'Fleet & condition'],
  ['people', 'People & teams'],
  ['parts', 'Parts & supply'],
  ['conditions', 'Ground, services & access'],
  ['work', 'Open work'],
  ['docs', 'Documents'],
] as const satisfies readonly (readonly [string, string])[];

/** Demo line 3195. A tab key on the site page. */
export type SiteTab = (typeof SITE_TABS)[number][0];

/**
 * Demo line 3193. `sent1` — the first sentence of a site-profile paragraph,
 * trailing punctuation and whitespace stripped, one full stop added back.
 */
export const sent1 = (t: string): string => `${String(t).split('. ')[0].replace(/[.\s]+$/, '')}.`;

// ===========================================================================
// Costs — the closeout workflow step
// ===========================================================================

/** Demo line 3659. Mean cost per operating hour across the whole base. */
export const meanCostPerOpHour = (): number =>
  Math.round(
    COST_PER_MACHINE.reduce((a, c) => a + c.cost_per_op_hour_aud, 0) / COST_PER_MACHINE.length,
  );

/**
 * Demo line 3659. The highest site cost per operating hour.
 *
 * Divides each site's cost rows by the number of **rows**, not by the machine
 * count — a different divisor from {@link fleetCostPerOpHourByMine}. Both are
 * the demo's and both appear on screen.
 */
export const highestSiteCostPerOpHour = (): number =>
  Math.max(
    ...MINE_ORDER.map((m) => {
      const ids = FLEET.filter((f) => f.mine === m).map((f) => f.machine_id);
      const c = COST_PER_MACHINE.filter((x) => ids.includes(x.machine_id));
      return Math.round(c.reduce((a, x) => a + x.cost_per_op_hour_aud, 0) / c.length);
    }),
  );

/** Demo line 3659. Share of all work orders that are not preventive, %. */
export const correctiveSharePct = (ctx: WorkOrderCtx): number =>
  Math.round(
    (ctx.workOrders.filter((w) => w.wo_type !== 'Preventive').length / ctx.workOrders.length) * 100,
  );

/** Demo line 3661. One row of "Cost to serve by site". */
export interface CostToServeRow {
  readonly mine: MineCode;
  readonly machines: number;
  /** 18-month ledger cost, AUD. */
  readonly total: number;
  readonly costPerOpHour: number;
  readonly labourRate: number;
  /** Share of the site's work orders that are not preventive, %. */
  readonly correctivePct: number;
}

/** Demo line 3661. `costToServeBySite`, data only. */
export const costToServeBySite = (ctx: WorkOrderCtx): CostToServeRow[] =>
  MINE_ORDER.map((m) => {
    const ids = FLEET.filter((f) => f.mine === m).map((f) => f.machine_id);
    const c = COST_PER_MACHINE.filter((x) => ids.includes(x.machine_id));
    return {
      mine: m,
      machines: ids.length,
      total: c.reduce((a, x) => a + x.amount_aud, 0),
      costPerOpHour: Math.round(c.reduce((a, x) => a + x.cost_per_op_hour_aud, 0) / c.length),
      labourRate: MINES[m].labour_rate,
      correctivePct: Math.round(
        (ctx.workOrders.filter((w) => w.mine === m && w.wo_type !== 'Preventive').length /
          ctx.workOrders.filter((w) => w.mine === m).length) *
          100,
      ),
    };
  });

/** Demo line 3663. One bar of "Where the cost sits". */
export interface CostCategoryTotal {
  readonly category: string;
  readonly amount: number;
}

/**
 * Demo line 3663. Category totals across the whole base, largest first.
 *
 * The demo read each row defensively as `r.category || r[1]` and
 * `r.amount_aud ?? r[2]`, because the same table reaches it in object and in
 * positional form. The port's `costs.json` is always objects, so only the
 * object read survives; the `|| 0` coercion on the amount is kept.
 */
export function costCategoryTotals(ctx: MachineCostCategoryCtx): CostCategoryTotal[] {
  const cats: Record<string, number> = {};
  ctx.costByMachineCat.forEach((r) => {
    const k = r.category;
    if (k == null) return;
    cats[k] = (cats[k] ?? 0) + (Number(r.amount_aud) || 0);
  });
  return Object.entries(cats)
    .sort((a, b) => b[1] - a[1])
    .map(([category, amount]) => ({ category, amount }));
}

/** Demo line 3664. The ten most expensive machines to run, worst first. */
export const topCostMachines = (): MachineCost[] =>
  COST_PER_MACHINE.slice()
    .sort((a, b) => b.cost_per_op_hour_aud - a.cost_per_op_hour_aud)
    .slice(0, 10);

// ===========================================================================
// Statistics
// ===========================================================================

/** Demo line 3964. `monthOf` — `'2026-09-03'` → `'2026-09'`. */
export const monthOf = (s: string): string => s.slice(0, 7);

/**
 * Demo line 3965. `slope` — the least-squares gradient of a series against its
 * own index, in units per step.
 *
 * Returns `0` for fewer than three points and `0` when the x-variance is zero.
 * Both guards are the demo's.
 */
export function slope(vals: readonly number[]): number {
  const n = vals.length;
  if (n < 3) return 0;
  const mx = (n - 1) / 2;
  const my = vals.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i += 1) {
    num += (i - mx) * (vals[i] - my);
    den += (i - mx) ** 2;
  }
  return den ? num / den : 0;
}
